/* ============================================================================
   MOTEUR DE RENDU CANVAS  (v2)
   ----------------------------------------------------------------------------
   Mise en page du schéma (toujours incluse dans le PNG exporté) :
     ┌───────────────────────────────────────────┐
     │  BANDEAU TITRE   [Match|Exercice] + titre   │
     ├───────────────────────────────────────────┤
     │                 TERRAIN (paysage)           │
     │   buts / paniers à GAUCHE et à DROITE        │
     ├───────────────────────────────────────────┤
     │  LÉGENDE  mouvements présents + repères      │
     └───────────────────────────────────────────┘
   Convention de coordonnées (partagée avec l'IA et le parser) :
     x,y,w,h ∈ [0..100] ; (0,0) = coin HAUT-GAUCHE du terrain.
     Le camp/but ATTAQUÉ est à DROITE (x=100), le camp de départ à GAUCHE (x=0).
   Deux modes : "color" (terrains réalistes) · "line" (tableau blanc / impression).
   ========================================================================== */
import { arrowDetour } from "./detour.js";

let canvas = null, ctx = null;
let W = 1000, H = 700;
let MODE = "color";
let LINE = "#ffffff";
let FIELD = { x: 24, y: 64, w: 952, h: 548 };
const PLAYER_R = 15;
export const PLAYER_RADIUS = PLAYER_R;
/* Géométrie du DERNIER rendu plein écran (renderScene) — sert au transform
   inverse canvas <-> terrain pour l'édition manuelle (drag). N'est PAS mis à
   jour par renderToCanvas (vignettes), qui restaure l'état. */
let MAIN = { fx: FIELD.x, fy: FIELD.y, fw: FIELD.w, fh: FIELD.h, hc: false };
const clamp01 = (v) => Math.max(0, Math.min(100, v));
/* ---- MODE DEMI-TERRAIN (meta.halfCourt === "right") ------------------------
   Le 👎 prod PROD1 : « le prompt demandait sur demi-terrain ! ». Principe :
   les COORDONNÉES DE SCÈNE restent sur 0-100 du terrain COMPLET (convention
   « but attaqué à droite ») — juges, éditeur, projection, schémas sauvegardés
   n'y voient AUCUNE différence. Seul l'AFFICHAGE transforme : la moitié droite
   (x ∈ [50,100]) est étirée sur toute la boîte de terrain, et le fond dessine
   le demi-terrain (ligne médiane au bord gauche, but/panier à droite) en
   traçant le terrain complet sur une boîte virtuelle double clippée.
   Sans le marqueur meta, ces deux fonctions sont l'identité : le rendu est
   bit-à-bit celui d'avant. */
/* REBONDS AUTOMATIQUES (tennis / tennis de table — 👎 prod : « il n'y a pas
   les impacts de rebond sur la table !! ») : au rendu, chaque frappe de balle
   qui retombe DANS le court/sur la table reçoit son repère d'impact, sans rien
   demander à l'IA ni à la scène (le moteur historique posait ses marqueurs
   `rebond` ; le mode scène ne le fait pas toujours). PURE : renvoie les points
   à dessiner — la scène n'est jamais modifiée, un marqueur `rebond` déjà posé
   à proximité (<5) n'est pas doublé. Jamais au badminton/volley (pas de rebond). */
export function autoRebondPoints(sport, segs, markers) {
  const RECT = sport === "tennis_de_table" ? { x0: 23, x1: 77, y0: 21, y1: 79 }
    : sport === "tennis" ? { x0: 11, x1: 89, y0: 12, y1: 88 } : null;
  if (!RECT) return [];
  const existing = (Array.isArray(markers) ? markers : [])
    .filter((m) => String(m.type || "").toLowerCase() === "rebond" && Number.isFinite(+m.x));
  const pts = [];
  for (const s of (segs || [])) {
    // Le TYPE d'un segment résolu vit dans `s.a` (l'action d'origine) — `s`
    // ne porte que `from`/`to`. Lire `s.type` renvoyait TOUJOURS undefined :
    // la boucle sortait à chaque tour et AUCUN rebond n'a jamais été dessiné
    // depuis l'introduction de la fonction (👎 prod 09/08 « nous avions réglé
    // depuis longtemps l'affichage des rebonds… pourquoi cela ne fonctionne
    // plus ? » — la vérité est qu'il n'a jamais fonctionné). Le test le
    // manquait car il fabriquait des segments À LA MAIN dans une forme que
    // `resolveActionSegments` ne produit pas ; il passe désormais par elle.
    const type = s.a?.type ?? s.type;
    if (type !== "renvoi" && type !== "tir") continue;
    const { x, y } = s.to || {};
    if (!Number.isFinite(x) || x <= RECT.x0 || x >= RECT.x1 || y <= RECT.y0 || y >= RECT.y1) continue;
    if (existing.some((m) => Math.hypot(+m.x - x, +m.y - y) < 5)) continue;
    if (pts.some((p) => Math.hypot(p.x - x, p.y - y) < 3)) continue;
    pts.push({ x, y });
    // SERVICE AU TENNIS DE TABLE : DEUX rebonds (👎 prod 09/08 « un service au
    // tennis de table rebondit sur la table du serveur ET de l'adversaire »).
    // Le premier impact, dans la demi-table du SERVEUR, est DÉRIVÉ ici : la
    // scène ne porte que la chute finale, côté adverse — l'IA n'a rien de plus
    // à placer, et le schéma montre la trajectoire réglementaire. On le pose
    // sur le segment, à mi-chemin entre le bord de table du serveur et le
    // filet, là où un service se pose réellement.
    if (sport === "tennis_de_table" && /service|engage/i.test(String(s.a?.label ?? ""))) {
      const fx = s.from?.x, fy = s.from?.y;
      if (Number.isFinite(fx) && Number.isFinite(fy) && (fx - 50) * (x - 50) < 0) {
        // Au TIERS de la demi-table du serveur, côté BORD — pas au milieu.
        // Deux raisons, l'une physique et l'autre de lisibilité : un service
        // rebondit près de son propre bord de table ; et le milieu du segment
        // est exactement là où le rendu pose la pastille de numéro d'étape,
        // qui recouvrait l'impact (constaté au rendu).
        const bx = fx < 50 ? RECT.x0 + (50 - RECT.x0) / 3 : RECT.x1 - (RECT.x1 - 50) / 3;
        const k = (bx - fx) / (x - fx);                                  // position sur le segment
        const by = fy + k * (y - fy);
        if (k > 0 && k < 1 && by > RECT.y0 && by < RECT.y1
          && !existing.some((m) => Math.hypot(+m.x - bx, +m.y - by) < 5)
          && !pts.some((p) => Math.hypot(p.x - bx, p.y - by) < 3)) pts.push({ x: bx, y: by });
      }
    }
  }
  return pts;
}

/* SENS D'UNE COTE — source de vérité UNIQUE, partagée par le rendu et par
   l'éditeur (tracé, test de clic, poignée, redimensionnement). Une cote porte
   soit "w" (horizontale, comportement historique), soit "h" (VERTICALE, 👎 prod
   08/08 : une largeur de couloir se lit en y). Sans "h", rien ne change. */
export function isVerticalCote(m) {
  return Number.isFinite(+m?.h) && +m.h > 0 && (!Number.isFinite(+m?.w) || +m.h > +m.w);
}

export function halfX(x) { return (x - 50) * 2; }        // terrain complet -> boîte affichée
export function invHalfX(x) { return x / 2 + 50; }       // boîte affichée  -> terrain complet
let HALF = false;   // posé par draw() depuis data.meta.halfCourt, jamais ailleurs

export function canvasToField(cx, cy) {
  const rx = clamp01(((cx - MAIN.fx) / MAIN.fw) * 100);
  return { x: MAIN.hc ? invHalfX(rx) : rx, y: clamp01(((cy - MAIN.fy) / MAIN.fh) * 100) };
}
export function fieldToCanvas(x, y) {
  const rx = MAIN.hc ? halfX(x) : x;
  return { x: MAIN.fx + (rx / 100) * MAIN.fw, y: MAIN.fy + (y / 100) * MAIN.fh };
}
/* Segments des flèches TELS QUE DESSINÉS au dernier rendu (positions évoluées
   par le suivi temporel), en coordonnées terrain. Sert à l'éditeur pour le
   hit-test des flèches et le placement de leurs poignées. */
let LAST_SEGS = [];
let TERRAIN_OPTS = {};   // options de fond de terrain (ex. brick ultimate), lues par TERRAINS
export function getLastSegments() { return LAST_SEGS; }
/* Hexagones d'ordre tels que placés au dernier rendu (coordonnées CANVAS) :
   l'éditeur les rend cliquables pour renuméroter les étapes. */
let LAST_BADGES = [];
export function getLastBadges() { return LAST_BADGES; }

/* Palette + STYLE DE TRAIT par type (notation EPS, lisible sans couleur) :
   déplacement = plein · passe = pointillés · dribble = ondulé · tir = épais ·
   écran = trait barré d'un T. */
const ACTIONS = {
  course:  { color: "#D95F02", style: "solid",  label: "Déplacement / course" },
  passe:   { color: "#0072B2", style: "dashed", label: "Passe" },
  renvoi:  { color: "#0072B2", style: "dashed", label: "Service / renvoi" },
  centre:  { color: "#E6AB02", style: "dashed", label: "Centre" },
  dribble: { color: "#1B9E77", style: "wavy",   label: "Conduite / dribble" },
  tir:     { color: "#7B5CD6", style: "thick",  label: "Tir / frappe" },
  pointer: { color: "#0E9594", style: "dashed", label: "Pointer / placer" },
  ecran:   { color: "#E7298A", style: "screen", label: "Écran / bloc" },
  rotation:{ color: "#5B6B86", style: "dashed", label: "Rotation des ateliers" },
};
function actionDef(type) { return ACTIONS[type] || ACTIONS.passe; }

export function initRenderer(canvasEl) {
  canvas = canvasEl; ctx = canvas.getContext("2d");
  W = canvas.width; H = canvas.height;
}
export function setRenderMode(mode) { MODE = mode === "line" ? "line" : "color"; }

function inkColor() { return MODE === "line" ? "#1E293B" : "#FFFFFF"; }
function markerStroke() { return MODE === "line" ? "#1E293B" : "#FFFFFF"; }
function bgForSport(sport) {
  if (MODE === "line") return "#FFFFFF";
  if (sport === "basketball" || sport === "basket3x3" || sport === "futsal") return "#C6904B";
  if (sport === "handball") return "#2A6FA0";
  if (sport === "volleyball") return "#C77A45";
  if (sport === "badminton") return "#3B7A9E";
  if (sport === "tennis") return "#3C7A52";
  if (sport === "tennis_de_table" || sport === "tennis de table" || sport === "pingpong") return "#1E5B8F";
  if (sport === "gymnastique" || sport === "gym") return "#3E6FA0";
  if (sport === "athletisme") return "#9E4636";                 // piste (brique)
  if (sport === "tir_a_l_arc") return "#3E7D4F";                // pas de tir (vert)
  if (sport === "petanque") return "#A8824E";                   // terrain en terre
  return "#2F8A4E"; // football / rugby / ultimate / other
}

function convert(x, y) {
  const hx = HALF ? halfX(+x) : +x;   // demi-terrain : la moitié droite s'étire sur la boîte
  const cx = Math.max(0, Math.min(100, hx)), cy = Math.max(0, Math.min(100, y));
  return { px: FIELD.x + (cx / 100) * FIELD.w, py: FIELD.y + (cy / 100) * FIELD.h };
}
function convertSize(w, h) { return { w: ((w * (HALF ? 2 : 1)) / 100) * FIELD.w, h: (h / 100) * FIELD.h }; }

/* --------------------------------------------------------------------------
   Primitives
   -------------------------------------------------------------------------- */
function roundRect(x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/* --------------------------------------------------------------------------
   Anti-chevauchement des étiquettes
   -------------------------------------------------------------------------- */
let placed = [];
function overlaps(a, b) {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}
function fits(box) {
  if (box.x < FIELD.x - 2 || box.y < FIELD.y - 2 ||
      box.x + box.w > FIELD.x + FIELD.w + 2 || box.y + box.h > FIELD.y + FIELD.h + 2) return false;
  return placed.every((p) => !overlaps(box, p));
}
/* ÉTIQUETTE SUR DEUX LIGNES plutôt que coupée net (salve proactive nº1 : 19
   étiquettes tronquées sur 40 scènes — « Réceptionneur arrière… », « Point
   valable seulemen »). Coupe au dernier ESPACE avant la limite ; ne tronque en
   dernier recours que si le texte déborde encore des `maxLines` lignes.
   PURE et exportée : testable sans canvas. Un libellé court (le cas de loin le
   plus fréquent) ressort inchangé, sur une seule ligne. */
export function wrapLabel(text, max = 22, maxLines = 2) {
  const s = String(text ?? "").trim().replace(/\s+/g, " ");
  if (!s) return [];
  if (s.length <= max) return [s];
  const lines = [];
  let rest = s;
  while (rest.length > max && lines.length < maxLines - 1) {
    let cut = rest.lastIndexOf(" ", max);
    if (cut <= 0) cut = max;              // mot unique plus long que la limite
    lines.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  lines.push(rest.length > max ? rest.slice(0, max - 1) + "…" : rest);
  return lines;
}
/* Place une étiquette près de (cx,cy) en essayant plusieurs ancrages. */
function placeLabel(text, cx, cy, opts = {}) {
  if (!text) return;
  const lines = wrapLabel(text, 22, opts.maxLines || 2);
  if (!lines.length) return;
  ctx.font = "600 12px system-ui, sans-serif";
  const tw = Math.max(...lines.map((l) => ctx.measureText(l).width));
  const w = tw + 12, h = 17 + (lines.length - 1) * 13;
  const gap = opts.gap != null ? opts.gap : PLAYER_R + 5;
  // Ancrages candidats : dessous, dessus, droite, gauche, DIAGONALES, puis loin.
  const cands = opts.cands || [
    [0, gap], [0, -gap - h], [gap, -h / 2], [-gap - w, -h / 2],
    [gap, gap], [-gap - w, gap], [gap, -gap - h], [-gap - w, -gap - h],
    [0, gap + h + 2], [0, -gap - 2 * h - 2],
  ];
  let chosen = null, best = null, bestCost = Infinity;
  for (const [dx, dy] of cands) {
    const box = { x: cx + dx - w / 2, y: cy + dy, w, h };
    if (fits(box)) { chosen = box; break; }
    const cost = labelCost(box);                      // sinon, on retient le MOINS chevauchant
    if (cost < bestCost) { bestCost = cost; best = box; }
  }
  if (!chosen) chosen = best || { x: cx - w / 2, y: cy + gap, w, h };
  placed.push(chosen);
  drawPill(lines, chosen.x, chosen.y, w, h, opts.fg || "#FFFFFF", opts.bg || "rgba(15,23,42,.72)");
}
/* Coût d'un emplacement non idéal : aire de chevauchement + forte pénalité
   hors-champ. Sert à choisir le « moins pire » quand aucun ancrage ne tient. */
function labelCost(box) {
  let cost = (Math.max(0, FIELD.x - box.x) + Math.max(0, box.x + box.w - (FIELD.x + FIELD.w))
    + Math.max(0, FIELD.y - box.y) + Math.max(0, box.y + box.h - (FIELD.y + FIELD.h))) * 40;
  for (const p of placed) {
    const ix = Math.min(box.x + box.w, p.x + p.w) - Math.max(box.x, p.x);
    const iy = Math.min(box.y + box.h, p.y + p.h) - Math.max(box.y, p.y);
    if (ix > 0 && iy > 0) cost += ix * iy;
  }
  return cost;
}
function drawPill(text, x, y, w, h, fg, bg) {
  const lines = Array.isArray(text) ? text : [text];
  ctx.fillStyle = bg; roundRect(x, y, w, h, 5); ctx.fill();
  ctx.fillStyle = fg; ctx.font = "600 12px system-ui, sans-serif";
  ctx.textAlign = "left"; ctx.textBaseline = "middle";
  // Une ligne : position historique (centrée). Plusieurs : empilées de 13 px.
  const y0 = y + h / 2 + 0.5 - (lines.length - 1) * 6.5;
  lines.forEach((l, i) => ctx.fillText(l, x + 6, y0 + i * 13));
}

/* --------------------------------------------------------------------------
   TERRAINS (paysage — buts/paniers à gauche & à droite)
   -------------------------------------------------------------------------- */
function drawFieldBg(sport) {
  // Fond de page (bandeaux) en blanc, aire de terrain colorée. Aire NEUTRE
  // (atelier hors sport) -> fond clair, pas la couleur d'un terrain.
  ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = TERRAIN_OPTS.plain ? (MODE === "line" ? "#FFFFFF" : "#EEF2F6") : bgForSport(sport);
  ctx.fillRect(FIELD.x, FIELD.y, FIELD.w, FIELD.h);
  LINE = (TERRAIN_OPTS.plain && MODE !== "line") ? "#64748B" : inkColor();   // lignes sombres sur l'aire neutre claire
  ctx.strokeStyle = LINE; ctx.lineWidth = 2.5; ctx.lineJoin = "round";
}
const TERRAINS = {
  football() {
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    line(f.x + f.w / 2, f.y, f.x + f.w / 2, f.y + f.h);
    circle(f.x + f.w / 2, f.y + f.h / 2, f.h * 0.13);
    dot(f.x + f.w / 2, f.y + f.h / 2);
    const bh = f.h * 0.55, bw = f.w * 0.13, gh = f.h * 0.26, gw = f.w * 0.05;
    ctx.strokeRect(f.x, f.y + (f.h - bh) / 2, bw, bh);
    ctx.strokeRect(f.x + f.w - bw, f.y + (f.h - bh) / 2, bw, bh);
    ctx.strokeRect(f.x, f.y + (f.h - gh) / 2, gw, gh);
    ctx.strokeRect(f.x + f.w - gw, f.y + (f.h - gh) / 2, gw, gh);
    goal("left"); goal("right");
  },
  futsal() {
    // Terrain type HANDBALL (surface de but en arc) mais SANS la ligne des 9 m,
    // avec les cages. Le pivot peut se placer en tête de surface, voire dedans.
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    line(f.x + f.w / 2, f.y, f.x + f.w / 2, f.y + f.h);
    circle(f.x + f.w / 2, f.y + f.h / 2, f.h * 0.13);
    dot(f.x + f.w / 2, f.y + f.h / 2);
    const cy = f.y + f.h / 2, r6 = f.h * 0.34;
    arc(f.x, cy, r6, -Math.PI / 2, Math.PI / 2);                          // surface de but (arc)
    arc(f.x + f.w, cy, r6, Math.PI / 2, Math.PI * 1.5);
    goal("left"); goal("right");
  },
  basketball() {
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    line(f.x + f.w / 2, f.y, f.x + f.w / 2, f.y + f.h);
    circle(f.x + f.w / 2, f.y + f.h / 2, f.h * 0.12);
    basketEnd("left"); basketEnd("right");
  },
  basket3x3() {
    // Demi-terrain (panier à DROITE), MÊME format paysage que les autres sports.
    // Tracés 3x3 : raquette + ligne à 2 points + ZONE DE NON-CHARGE sous le
    // panier. PAS de cercle en tête de raquette (n'existe pas en 3x3).
    const f = FIELD, cy = f.y + f.h / 2;
    ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    const baseX = f.x + f.w;
    const keyW = f.w * 0.18, keyH = f.h * 0.34, tpR = f.h * 0.46;
    ctx.lineWidth = 1.8;
    ctx.strokeRect(baseX - keyW, cy - keyH / 2, keyW, keyH);            // raquette
    const k = Math.PI / 2 - Math.PI / 2.3;
    arc(baseX, cy, tpR, Math.PI / 2 + k, Math.PI * 1.5 - k);            // ligne à 2 points (6,75 m)
    const hoopX = baseX - 14;
    ctx.beginPath(); ctx.arc(hoopX, cy, 6, 0, Math.PI * 2); ctx.stroke();   // cerceau
    arc(hoopX, cy, f.h * 0.10, Math.PI / 2, Math.PI * 1.5);            // zone de non-charge (demi-cercle)
  },
  handball() {
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    line(f.x + f.w / 2, f.y, f.x + f.w / 2, f.y + f.h);
    const cy = f.y + f.h / 2, r6 = f.h * 0.34;
    arc(f.x, cy, r6, -Math.PI / 2, Math.PI / 2);
    arc(f.x + f.w, cy, r6, Math.PI / 2, Math.PI * 1.5);
    ctx.setLineDash([7, 6]);
    arc(f.x, cy, f.h * 0.47, -Math.PI / 2, Math.PI / 2);
    arc(f.x + f.w, cy, f.h * 0.47, Math.PI / 2, Math.PI * 1.5);
    ctx.setLineDash([]);
    // MARQUE DES 7 M (salve handball nº1) : petit trait à x=23/x=77 — le
    // tireur de jet de 7 mètres a désormais un repère visible, ENTRE la zone
    // et les 9 m, comme sur un vrai terrain.
    const sevenH = f.h * 0.055;
    line(f.x + 0.23 * f.w, cy - sevenH, f.x + 0.23 * f.w, cy + sevenH);
    line(f.x + 0.77 * f.w, cy - sevenH, f.x + 0.77 * f.w, cy + sevenH);
    goal("left"); goal("right");
  },
  rugby() {
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    line(f.x + f.w / 2, f.y, f.x + f.w / 2, f.y + f.h);
    const ig = f.w * 0.10;
    line(f.x + ig, f.y, f.x + ig, f.y + f.h);
    line(f.x + f.w - ig, f.y, f.x + f.w - ig, f.y + f.h);
    ctx.setLineDash([9, 7]);
    line(f.x + f.w * 0.28, f.y, f.x + f.w * 0.28, f.y + f.h);
    line(f.x + f.w * 0.72, f.y, f.x + f.w * 0.72, f.y + f.h);
    ctx.setLineDash([]);
    rugbyPosts("left"); rugbyPosts("right");   // poteaux de marque (H) sur les lignes d'essai
  },
  badminton() {
    const f = FIELD, cx = f.x + f.w / 2; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    ctx.lineWidth = 3.5; line(cx, f.y, cx, f.y + f.h); ctx.lineWidth = 2;     // filet
    const back = f.w * 0.06, sv = f.w * 0.16, tram = f.h * 0.07, mid = f.y + f.h / 2;
    line(f.x + back, f.y, f.x + back, f.y + f.h);                            // ligne de fond service double
    line(f.x + f.w - back, f.y, f.x + f.w - back, f.y + f.h);
    line(cx - sv, f.y, cx - sv, f.y + f.h);                                  // lignes de service courtes
    line(cx + sv, f.y, cx + sv, f.y + f.h);
    line(f.x, f.y + tram, f.x + f.w, f.y + tram);                            // couloirs (simple)
    line(f.x, f.y + f.h - tram, f.x + f.w, f.y + f.h - tram);
    line(f.x + back, mid, cx - sv, mid);                                     // ligne centrale de service
    line(cx + sv, mid, f.x + f.w - back, mid);
  },
  volleyball() {
    // Le COURT (18 m) est inséré dans le champ : on laisse une ZONE LIBRE de
    // part et d'autre (derrière chaque ligne de fond) — le serveur s'y place
    // réglementairement pour engager. Lignes de fond à ~x10 / ~x90 (scène).
    const f = FIELD, cx = f.x + f.w / 2; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    const m = f.w * 0.10;                                                     // zone libre (derrière la ligne de fond)
    const cl = f.x + m, cw = f.w - 2 * m;
    ctx.strokeRect(cl, f.y, cw, f.h);                                         // limites du court
    ctx.lineWidth = 4; line(cx, f.y - 6, cx, f.y + f.h + 6); ctx.lineWidth = 2; // filet (déborde)
    const att = (cw / 2) / 3;                                                 // lignes d'attaque (3 m du filet)
    ctx.setLineDash([8, 6]);
    line(cx - att, f.y, cx - att, f.y + f.h);
    line(cx + att, f.y, cx + att, f.y + f.h);
    ctx.setLineDash([]);
  },
  tennis() {
    // Le court est INSÉRÉ : une marge (zone libre) subsiste derrière chaque
    // ligne de fond (bords gauche/droit) — le serveur s'y place pour servir.
    const f = FIELD, cx = f.x + f.w / 2; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    const m = f.w * 0.10, xL = f.x + m, xR = f.x + f.w - m;
    ctx.strokeRect(xL, f.y, f.w - 2 * m, f.h);                               // court (lignes de fond = bords du court)
    ctx.lineWidth = 3; line(cx, f.y, cx, f.y + f.h); ctx.lineWidth = 2;       // filet
    const tram = f.h * 0.11, sv = (cx - xL) * 0.55, mid = f.y + f.h / 2;
    line(xL, f.y + tram, xR, f.y + tram);                                    // couloirs simples
    line(xL, f.y + f.h - tram, xR, f.y + f.h - tram);
    line(cx - sv, f.y + tram, cx - sv, f.y + f.h - tram);                    // lignes de service
    line(cx + sv, f.y + tram, cx + sv, f.y + f.h - tram);
    line(cx - sv, mid, cx + sv, mid);                                        // ligne médiane de service
  },
  tennis_de_table() {
    // Vue de dessus : la TABLE (pas un terrain pleine page) posée au centre,
    // sol dégagé autour — les joueurs évoluent À L'EXTÉRIEUR de la table.
    const f = FIELD, cx = f.x + f.w / 2;
    const tx = f.x + f.w * 0.22, tw = f.w * 0.56, ty = f.y + f.h * 0.20, th = f.h * 0.60;
    ctx.fillStyle = MODE === "line" ? "rgba(15,23,42,.05)" : "#0E4A75";
    ctx.fillRect(tx, ty, tw, th);
    ctx.strokeStyle = LINE; ctx.lineWidth = 3;
    ctx.strokeRect(tx, ty, tw, th);
    ctx.setLineDash([6, 6]); ctx.lineWidth = 1.6;
    line(tx, ty + th / 2, tx + tw, ty + th / 2);                              // ligne centrale
    ctx.setLineDash([]);
    ctx.lineWidth = 4; line(cx, ty - 10, cx, ty + th + 10); ctx.lineWidth = 2.5; // filet + poteaux
    dot(cx, ty - 10); dot(cx, ty + th + 10);
  },
  ultimate() {
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    const ez = f.w * 0.18;                                                  // zones d'en-but
    line(f.x + ez, f.y, f.x + ez, f.y + f.h);
    line(f.x + f.w - ez, f.y, f.x + f.w - ez, f.y + f.h);
    // Marques "brick" (remise en jeu) : détail réglementaire, masqué par défaut
    // pour la lisibilité — affiché seulement si le texte les demande.
    if (TERRAIN_OPTS.brick) {
      const my = f.y + f.h / 2;
      [f.x + f.w * 0.32, f.x + f.w * 0.68].forEach((bx) => {
        line(bx - 5, my - 5, bx + 5, my + 5); line(bx - 5, my + 5, bx + 5, my - 5);
      });
    }
  },
  gymnastique() {
    // Praticable : aire bordée + diagonales (lignes de passage / enchaînement).
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    const m = Math.min(f.w, f.h) * 0.06;                                    // liseré intérieur
    ctx.lineWidth = 1.5; ctx.strokeRect(f.x + m, f.y + m, f.w - 2 * m, f.h - 2 * m);
    ctx.setLineDash([10, 8]); ctx.lineWidth = 1.5;
    line(f.x + m, f.y + m, f.x + f.w - m, f.y + f.h - m);                   // diagonales
    line(f.x + f.w - m, f.y + m, f.x + m, f.y + f.h - m);
    ctx.setLineDash([]);
  },
  athletisme() {
    const f = FIELD; ctx.strokeStyle = LINE;
    // STADE COMPLET (terrainOpts.stade — posé seulement si le texte l'exige :
    // tour de piste, virage, 200 m et plus, relais 4×100) : anneau à 4 couloirs
    // autour d'une PELOUSE centrale. Le tracé par défaut, en ligne droite,
    // reste celui de tous les sprints et ateliers — un 100 m se court droit.
    if (TERRAIN_OPTS.stade) {
      // Forme de stade = rectangle aux extrémités semi-circulaires. On la trace
      // du plus grand au plus petit : anneau extérieur, couloirs, corde
      // intérieure, puis la pelouse remplie au centre.
      const stadium = (inset) => {
        const x = f.x + inset, y = f.y + inset;
        const w = Math.max(4, f.w - inset * 2), h = Math.max(4, f.h - inset * 2);
        const r = h / 2;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + w - r, y);
        ctx.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
        ctx.lineTo(x + r, y + h);
        ctx.arc(x + r, y + r, r, Math.PI / 2, Math.PI * 1.5);
        ctx.closePath();
      };
      const band = f.h * 0.16;                 // largeur totale des 4 couloirs
      // PELOUSE centrale (aire de sauts/lancers) — remplie avant les traits.
      stadium(band);
      ctx.fillStyle = MODE === "line" ? "#FFFFFF" : "#3E7D4F";
      ctx.fill();
      ctx.lineWidth = 2.5; stadium(0); ctx.stroke();          // corde extérieure
      ctx.lineWidth = 2.5; stadium(band); ctx.stroke();       // corde intérieure
      ctx.lineWidth = 1.2;
      for (let i = 1; i < 4; i++) { stadium((band * i) / 4); ctx.stroke(); }
      // Ligne d'arrivée : en haut de la ligne droite avant (convention piste),
      // tracée en travers des seuls couloirs.
      ctx.lineWidth = 3;
      const ax = f.x + f.w * 0.72;
      line(ax, f.y + f.h - band, ax, f.y + f.h);
      return;
    }
    // Piste : couloirs horizontaux (sens de la course) + ligne d'arrivée à droite.
    ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    const lanes = 4;
    ctx.lineWidth = 1.5;
    for (let i = 1; i < lanes; i++) line(f.x, f.y + (i / lanes) * f.h, f.x + f.w, f.y + (i / lanes) * f.h);
    ctx.lineWidth = 3; line(f.x + f.w - 5, f.y, f.x + f.w - 5, f.y + f.h);     // arrivée
  },
  tir_a_l_arc() {
    // Pas de tir à gauche (ligne pointillée) + cible en anneaux à droite. En mode
    // ATELIERS (terrainOpts.stations), les cibles sont dessinées par poste
    // (markers) : on n'ajoute PAS la cible centrale unique.
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    const lx = f.x + f.w * 0.12;
    ctx.setLineDash([8, 6]); line(lx, f.y, lx, f.y + f.h); ctx.setLineDash([]);
    if (TERRAIN_OPTS.stations) return;
    const tx = f.x + f.w * 0.9, cy = f.y + f.h / 2;
    ctx.lineWidth = 2;
    for (let k = 4; k >= 1; k--) circle(tx, cy, k * 0.07 * f.h);
    ctx.fillStyle = MODE === "line" ? "#1E293B" : "#E6AB02";
    ctx.beginPath(); ctx.arc(tx, cy, 0.07 * f.h * 0.5, 0, Math.PI * 2); ctx.fill();
  },
  petanque() {
    // Terrain en terre : cadre + cercle de lancer à gauche.
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    circle(f.x + f.w * 0.1, f.y + f.h / 2, f.h * 0.08);                        // cercle de lancer
  },
  other() {
    const f = FIELD; ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.strokeRect(f.x, f.y, f.w, f.h);
    line(f.x + f.w / 2, f.y, f.x + f.w / 2, f.y + f.h);
  },
};
TERRAINS.pingpong = TERRAINS.tennis_de_table;
TERRAINS.gym = TERRAINS.gymnastique;
function line(x1, y1, x2, y2) { ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); }
/* ---- ARCS DU FOND EN MODE DEMI-TERRAIN (👎 prod 08/08) ---------------------
   Le fond du demi-terrain est tracé sur une boîte virtuelle DEUX FOIS plus
   large (draw()), mais sa HAUTEUR n'est pas doublée. Or les cercles et arcs des
   terrains ont leur rayon exprimé en fraction de `f.h` : dessinés comme des
   CERCLES, ils gardent ce rayon en y et se retrouvent deux fois trop PLATS en
   x, tandis que les tracés rectangulaires (fractions de `f.w`) doublent, eux,
   correctement. Les deux 👎 du lot le disent depuis les deux bouts :
     · basket   « la raquette dépasse la zone des 3 points » — la raquette
       (0,18 × f.w, doublée) sort de l'arc à 3 points (0,46 × f.h, inchangé) ;
     · handball « les places devraient être déterminées par rapport à la zone
       des 6 mètres puis la ligne des 9 » — les deux arcs sont écrasés contre
       le but et deux fois trop rapprochés : les repères qui servent JUSTEMENT
       à placer les joueurs sont faux.
   Correctif : pendant ce seul passage, les arcs deviennent des ELLIPSES de
   rayon x doublé — soit exactement les proportions du terrain complet, qui est
   la référence acceptée (et celle que `regulation.js` lit). Hors demi-terrain
   `HALF_BG` reste faux et le tracé emprunte la même branche `ctx.arc` qu'avant,
   donc bit-à-bit identique — la promesse du mode demi-terrain est tenue. */
let HALF_BG = false;
function circle(cx, cy, r) {
  ctx.beginPath();
  if (HALF_BG) ctx.ellipse(cx, cy, r * 2, r, 0, 0, Math.PI * 2); else ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
}
function dot(cx, cy) { ctx.fillStyle = LINE; ctx.beginPath(); ctx.arc(cx, cy, 2.6, 0, Math.PI * 2); ctx.fill(); }
function arc(cx, cy, r, a0, a1) {
  ctx.beginPath();
  if (HALF_BG) ctx.ellipse(cx, cy, r * 2, r, 0, a0, a1); else ctx.arc(cx, cy, r, a0, a1);
  ctx.stroke();
}
function goal(side) {
  // Cage VISIBLE. Elle est dessinée sur la MARGE (hors aire colorée) -> couleur
  // SOMBRE (du blanc serait invisible sur le fond blanc de la marge).
  const f = FIELD, gh = f.h * 0.20, cy = f.y + f.h / 2, d = 9;
  const lineX = side === "left" ? f.x : f.x + f.w;
  const bx = side === "left" ? lineX - d : lineX;
  ctx.save();
  ctx.strokeStyle = "#334155"; ctx.lineWidth = 2.2;
  ctx.strokeRect(bx, cy - gh / 2, d, gh);
  ctx.lineWidth = 0.7; ctx.strokeStyle = "#94A3B8";
  for (let i = 1; i < 3; i++) line(bx + (d * i) / 3, cy - gh / 2, bx + (d * i) / 3, cy + gh / 2);
  for (let j = 1; j < 3; j++) line(bx, cy - gh / 2 + (gh * j) / 3, bx + d, cy - gh / 2 + (gh * j) / 3);
  ctx.strokeStyle = "#334155"; ctx.lineWidth = 4.5; ctx.lineCap = "round";
  line(lineX, cy - gh / 2, lineX, cy + gh / 2);   // bouche du but (poteaux), bien visible
  ctx.restore();
}
/* Poteaux de rugby (en H) vus de dessus, posés sur la LIGNE D'ESSAI (à ig de
   chaque bord) : barre transversale le long de la ligne + deux montants courts
   pointant vers le terrain, embouts marqués. Couleur de marquage (visible en
   modes couleur et trait). */
function rugbyPosts(side) {
  const f = FIELD, ig = f.w * 0.10, cy = f.y + f.h / 2;
  const x = side === "left" ? f.x + ig : f.x + f.w - ig;
  const span = f.h * 0.12, dir = side === "left" ? 1 : -1, arm = f.w * 0.02;
  ctx.save();
  ctx.strokeStyle = LINE; ctx.lineWidth = 3.4; ctx.lineCap = "round";
  line(x, cy - span / 2, x, cy + span / 2);                        // barre transversale (entre les poteaux)
  line(x, cy - span / 2, x + dir * arm, cy - span / 2);           // montant haut
  line(x, cy + span / 2, x + dir * arm, cy + span / 2);           // montant bas
  dot(x + dir * arm, cy - span / 2); dot(x + dir * arm, cy + span / 2);
  ctx.restore();
}
function basketEnd(side) {
  const f = FIELD, cy = f.y + f.h / 2;
  const baseX = side === "left" ? f.x : f.x + f.w;
  const dir = side === "left" ? 1 : -1;          // vers l'intérieur
  const keyW = f.w * 0.18, keyH = f.h * 0.34;
  const ftR = f.h * 0.12, tpR = f.h * 0.46;
  // Raquette
  ctx.strokeRect(side === "left" ? baseX : baseX - keyW, cy - keyH / 2, keyW, keyH);
  // Cercle des lancers-francs
  const ftx = baseX + dir * keyW;
  arc(ftx, cy, ftR, side === "left" ? -Math.PI / 2 : Math.PI / 2, side === "left" ? Math.PI / 2 : Math.PI * 1.5);
  // Ligne à 3 points (arc) + cerceau
  arc(baseX, cy, tpR, side === "left" ? -Math.PI / 2.3 : Math.PI / 2 + (Math.PI / 2 - Math.PI / 2.3),
      side === "left" ? Math.PI / 2.3 : Math.PI * 1.5 - (Math.PI / 2 - Math.PI / 2.3));
  ctx.beginPath(); ctx.arc(baseX + dir * 14, cy, 6, 0, Math.PI * 2); ctx.stroke();
}

/* --------------------------------------------------------------------------
   Entités
   -------------------------------------------------------------------------- */
function teamColor(p, i) {
  // Couleur dérivée de l'ÉQUIPE (pas de la couleur par-joueur que l'IA met
  // parfois de façon incohérente : mêmes coéquipiers -> même couleur).
  // EXCEPTION : les PERSONNES (équipe OBS — observateur, arbitre, coach)
  // honorent p.color, choisie explicitement dans l'éditeur.
  if (p.team === "OBS") return p.color || "#F2C200";
  if (p.team === "B") return "#D55E00";
  // Troisième équipe : les confrontations à 3 existent (relais scolaire à
  // trois équipes en couloirs voisins). Vert Okabe-Ito, sûr en daltonisme.
  if (p.team === "C") return "#009E73";
  if (p.team === "k" || /gardien|keeper/i.test(p.role || "")) return "#F2C200";
  return "#0072B2";
}
function isLightFill(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
}
/* Numéro affiché dans la pastille : num explicite (éditable) sinon chiffres de
   l'id, sinon le rang. Une chaîne vide (num === "") laisse la pastille nue. */
function displayNum(p, i) {
  if (p.num != null) return String(p.num);
  // Un gardien / observateur / arbitre sans numéro explicite ne porte PAS de
  // numéro de champ fabriqué (identifié par sa couleur + son étiquette).
  if (p && (p.team === "k" || p.team === "OBS" || /gardien|keeper|observ|arbitre|chrono|starter/i.test(p.role || ""))) return "";
  const m = String(p.id || "").match(/\d+/);
  return m ? m[0] : String((i || 0) + 1);
}

/* Aires MATÉRIALISÉES (zone.fill) : sable (longueur), tapis (hauteur/gym),
   aire de lancer — reconnaissables d'un coup d'œil, sobres en mode trait. */
const ZONE_FILLS = { sable: "#EDD9A3", tapis: "#7FB0D6", aire: "rgba(255,255,255,.18)" };
/* Contour d'une zone « bande » : ruban d'épaisseur constante suivant une
   polyligne (z.band). Sert aux zones qui épousent une COURBE — la zone de
   transmission d'un relais suit le couloir, un rectangle droit la ferait
   déborder dans la pelouse. Renvoie le polygone fermé, en pixels. */
function bandPolygon(band, thick) {
  const pts = band.map((q) => convert(q.x, q.y));
  const th = convertSize(0, thick || 5).h;
  const left = [], right = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
    let nx = -(b.py - a.py), ny = b.px - a.px;
    const L = Math.hypot(nx, ny) || 1;
    nx = (nx / L) * (th / 2); ny = (ny / L) * (th / 2);
    left.push({ x: pts[i].px + nx, y: pts[i].py + ny });
    right.push({ x: pts[i].px - nx, y: pts[i].py - ny });
  }
  return left.concat(right.reverse());
}

function renderZones(zones) {
  zones.forEach((z) => {
    if (Array.isArray(z.band) && z.band.length >= 2) {
      const poly = bandPolygon(z.band, z.thick);
      ctx.beginPath();
      poly.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)));
      ctx.closePath();
      ctx.fillStyle = MODE === "line" ? "rgba(15,23,42,.10)" : "rgba(255,255,255,.20)";
      ctx.fill();
      ctx.strokeStyle = z.color || (MODE === "line" ? "#64748B" : "rgba(255,255,255,.95)");
      ctx.lineWidth = 2; ctx.setLineDash(z.style === "solid" ? [] : [9, 6]);
      ctx.stroke(); ctx.setLineDash([]);
      return;
    }
    const p = convert(z.x, z.y), s = convertSize(z.w, z.h);
    ctx.fillStyle = z.fill
      ? (MODE === "line" ? "rgba(15,23,42,.08)" : (ZONE_FILLS[z.fill] || ZONE_FILLS.aire))
      : (MODE === "line" ? "rgba(15,23,42,.035)" : "rgba(255,255,255,.06)");
    ctx.fillRect(p.px, p.py, s.w, s.h);
    // Sur une AIRE NEUTRE claire (dispositif/atelier hors sport), des lignes
    // blanches seraient invisibles : on trace en ardoise foncée pour le contraste.
    // Sur un terrain coloré (vert…), le blanc reste le bon choix.
    ctx.strokeStyle = z.color || (MODE === "line" ? "#64748B" : (TERRAIN_OPTS.plain ? "#475569" : "rgba(255,255,255,.85)"));
    ctx.lineWidth = 2; ctx.setLineDash(z.style === "solid" ? [] : [9, 6]);
    ctx.strokeRect(p.px, p.py, s.w, s.h);
    ctx.setLineDash([]);
  });
  // Étiquettes après les rectangles (pour la gestion des collisions).
  // Les zones en BANDE sont traitées à part, PLUS TARD (renderBandZoneLabels) :
  // elles cohabitent avec des pastilles serrées (couloirs voisins d'une piste)
  // qui ne sont des obstacles connus qu'une fois les joueurs enregistrés.
  zones.forEach((z) => {
    if (Array.isArray(z.band) && z.band.length >= 2) return;
    const p = convert(z.x, z.y), s = convertSize(z.w, z.h);
    if (z.label) placeLabel(z.label, p.px + 4, p.py + 2, { gap: 0, cands: [[40, 2], [40, s.h - 19]] });
    if (z.dim) placeLabel("⟷ " + z.dim, p.px + s.w / 2, p.py + 2,
      { gap: 0, fg: "#0F172A", bg: "#FDE68A", cands: [[0, s.h - 19], [0, 2]] });
  });
}

/* Étiquettes des zones en BANDE, posées APRÈS l'enregistrement des pastilles
   ET des flèches comme obstacles : sur une piste à trois couloirs occupés, un
   libellé posé trop tôt atterrissait sur le coureur du couloir voisin. Ancrage au milieu du
   ruban, poussé vers l'INTÉRIEUR du tracé — à l'extérieur d'un virage, c'est
   le bord de l'image, et le libellé s'y faisait rogner. */
function renderBandZoneLabels(zones) {
  zones.forEach((z) => {
    if (!z.label || !Array.isArray(z.band) || z.band.length < 2) return;
    const q = z.band[Math.floor(z.band.length / 2)];
    const m = convert(q.x, q.y), c = convert(50, 50);
    const th = convertSize(0, z.thick || 5).h;
    const L = Math.hypot(c.px - m.px, c.py - m.py) || 1;
    const off = th / 2 + 14;
    placeLabel(z.label, m.px + ((c.px - m.px) / L) * off, m.py + ((c.py - m.py) / L) * off,
      { gap: 2, cands: [[0, 0], [0, -20], [0, 20], [-46, 0], [46, 0], [0, -40], [0, 40]] });
  });
}

function drawMarker(m) {
  const { px, py } = convert(m.x, m.y);
  const type = (m.type || "").toLowerCase();
  if (["plot", "cone", "cône", "plots", "coupelle"].includes(type)) {
    // Couleur PERSONNALISABLE (éditeur) ; orange par défaut. Le mode trait
    // garde un contour foncé pour rester lisible en noir et blanc.
    ctx.fillStyle = MODE === "line" ? "#FFFFFF" : (m.color || "#EA7317");
    ctx.strokeStyle = MODE === "line" ? inkColor() : markerStroke(); ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.moveTo(px, py - 9); ctx.lineTo(px - 8, py + 7); ctx.lineTo(px + 8, py + 7);
    ctx.closePath(); ctx.fill(); ctx.stroke();
  } else if (["ballon", "ball", "balle"].includes(type)) {
    ctx.fillStyle = "#fff"; ctx.strokeStyle = "#111"; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#111"; ctx.beginPath(); ctx.arc(px, py, 2.2, 0, Math.PI * 2); ctx.fill();
  } else if (["disque", "frisbee"].includes(type)) {
    // Vue de dessus : disque = ellipse aplatie (anneau).
    ctx.save(); ctx.translate(px, py); ctx.scale(1, 0.5);
    ctx.fillStyle = "#fff"; ctx.strokeStyle = "#111"; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.strokeStyle = "#111"; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(0, 0, 4, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  } else if (["but", "goal", "minibut", "cage", "panier", "but-attaque"].includes(type)) {
    // Dimensions LIBRES via m.w/m.h (unités terrain, éditeur) ; sans elles,
    // gabarit historique 30×12 px (scènes IA / parser existantes).
    const s = (m.w != null || m.h != null) ? convertSize(m.w || 3, m.h || 2) : { w: 30, h: 12 };
    ctx.strokeStyle = inkColor(); ctx.lineWidth = 3; ctx.strokeRect(px - s.w / 2, py - s.h / 2, s.w, s.h);
  } else if (["cible", "target"].includes(type)) {
    ctx.strokeStyle = "#E6AB02"; ctx.lineWidth = 2.4;
    circle(px, py, 9); circle(px, py, 4);
    ctx.fillStyle = "#E6AB02"; ctx.beginPath(); ctx.arc(px, py, 2, 0, Math.PI * 2); ctx.fill();   // centre (jaune)
  } else if (["cible_visee", "cible_detail"].includes(type)) {
    // ENCART (générique) : cible détaillée en anneaux concentriques, avec la
    // ZONE VISÉE (centre) mise en avant + croix de visée. Palette archerie par
    // défaut (blanc·noir·bleu·rouge·jaune), surchargeable via m.rings. Sert de
    // « légende cible » réutilisable (tir à l'arc, vortex, cônes…).
    const R = 27;
    const rings = (Array.isArray(m.rings) && m.rings.length) ? m.rings
      : ["#FFFFFF", "#2B2B2B", "#2D6BB0", "#D7322B", "#F2C200"];
    const n = rings.length, rc = R / n;
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,.94)"; ctx.strokeStyle = "rgba(15,23,42,.35)"; ctx.lineWidth = 1.4;
    roundRect(px - R - 9, py - R - 9, (R + 9) * 2, (R + 9) * 2 + 18, 8); ctx.fill(); ctx.stroke();
    for (let k = 0; k < n; k++) {                                   // anneaux, du plus grand au centre
      ctx.beginPath(); ctx.arc(px, py, R * (n - k) / n, 0, Math.PI * 2);
      ctx.fillStyle = MODE === "line" ? (k % 2 ? "#FFFFFF" : "#1A1A1A") : rings[k];
      ctx.fill(); ctx.strokeStyle = "rgba(0,0,0,.32)"; ctx.lineWidth = 0.7; ctx.stroke();
    }
    ctx.strokeStyle = MODE === "line" ? "#000" : "#0F172A"; ctx.lineWidth = 2;   // halo de la zone visée
    ctx.beginPath(); ctx.arc(px, py, rc + 3, 0, Math.PI * 2); ctx.stroke();
    ctx.lineWidth = 1; line(px - rc - 6, py, px + rc + 6, py); line(px, py - rc - 6, px, py + rc + 6);   // croix de visée
    ctx.fillStyle = "#0F172A"; ctx.font = "600 12px system-ui, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(m.text || "Zone visée", px, py + R + 10);
    ctx.restore();
  } else if (type === "haie") {
    // Haie d'athlétisme vue de dessus : barre EN TRAVERS du couloir (verticale),
    // avec deux petits pieds. Couleur sobre, distincte des plots orange.
    const hh = 13;
    ctx.strokeStyle = MODE === "line" ? inkColor() : "#374151"; ctx.lineWidth = 3;
    line(px, py - hh, px, py + hh);
    ctx.lineWidth = 1.6;
    line(px - 4, py - hh, px + 4, py - hh); line(px - 4, py + hh, px + 4, py + hh);
  } else if (type === "station") {
    // Station d'un parcours (gym) : anneau numéroté, distinct des pastilles joueur.
    ctx.fillStyle = MODE === "line" ? "#FFFFFF" : "rgba(255,255,255,.92)";
    ctx.strokeStyle = "#0072B2"; ctx.lineWidth = 2.6;
    ctx.beginPath(); ctx.arc(px, py, 12, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#0072B2"; ctx.font = "700 13px system-ui, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(String(m.n ?? ""), px, py + 0.5);
    placed.push({ x: px - 12, y: py - 12, w: 24, h: 24 });
  } else if (type === "essai") {
    // Essai (rugby) / réception en zone (ultimate) : ballon ovale APLATI au sol,
    // surmonté d'une flèche descendante (le geste d'aplatir / de capter).
    ctx.fillStyle = "#fff"; ctx.strokeStyle = "#111"; ctx.lineWidth = 1.6;
    ctx.save(); ctx.translate(px, py); ctx.scale(1.35, 0.78);
    ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); ctx.restore();
    ctx.strokeStyle = inkColor(); ctx.lineWidth = 2.4;
    line(px, py - 17, px, py - 7);
    ctx.beginPath(); ctx.moveTo(px - 4, py - 10); ctx.lineTo(px, py - 6); ctx.lineTo(px + 4, py - 10); ctx.stroke();
  } else if (type === "contact") {
    // Contact / plaquage (duel rugby, choc) : étoile d'impact (rayons) marquant
    // le point de rencontre porteur / défenseur.
    ctx.strokeStyle = MODE === "line" ? inkColor() : "#D55E00"; ctx.lineWidth = 2.2; ctx.lineCap = "round";
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2, r0 = k % 2 ? 3 : 5, r1 = k % 2 ? 8 : 11;
      line(px + Math.cos(a) * r0, py + Math.sin(a) * r0, px + Math.cos(a) * r1, py + Math.sin(a) * r1);
    }
  } else if (type === "cerceau") {
    // Cerceau (matériel EPS) : anneau coloré, personnalisable via m.color.
    ctx.strokeStyle = MODE === "line" ? inkColor() : (m.color || "#DC2626");
    ctx.lineWidth = 3.2;
    ctx.beginPath(); ctx.arc(px, py, 10, 0, Math.PI * 2); ctx.stroke();
  } else if (type === "casier" || type === "caisse") {
    // Casier / caisse à ballons (la réserve du serveur / lanceur) : petite caisse
    // trapézoïdale surmontée de trois ballons qui dépassent. Couleur via m.color.
    ctx.save();
    ctx.fillStyle = MODE === "line" ? "#FFFFFF" : (m.color || "#B45309");
    ctx.strokeStyle = markerStroke(); ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(px - 11, py + 8); ctx.lineTo(px + 11, py + 8);
    ctx.lineTo(px + 8, py - 2); ctx.lineTo(px - 8, py - 2);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#FFFFFF"; ctx.lineWidth = 1.2;
    for (const [dx, dy] of [[-5, -3], [0, -5], [5, -3]]) {
      ctx.beginPath(); ctx.arc(px + dx, py + dy, 3.2, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    ctx.restore();
  } else if (type === "boule") {
    // Couleur PAR ÉQUIPE si fournie (m.color), sinon neutre (boule générique).
    ctx.fillStyle = MODE === "line" ? "#FFFFFF" : (m.color || "#9AA3AD");
    ctx.strokeStyle = MODE === "line" ? inkColor() : "rgba(0,0,0,.4)"; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  } else if (type === "cochonnet") {
    ctx.fillStyle = "#E6AB02"; ctx.strokeStyle = markerStroke(); ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.arc(px, py, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  } else if (type === "temoin") {
    ctx.save(); ctx.translate(px, py); ctx.rotate(-Math.PI / 5);
    ctx.fillStyle = "#D55E00"; ctx.strokeStyle = markerStroke(); ctx.lineWidth = 1.2;
    roundRect(-9, -2.5, 18, 5, 2.5); ctx.fill(); ctx.stroke(); ctx.restore();
  } else if (type === "barre") {
    // Saut en hauteur, VUE DE DESSUS : la barre court sur la LONGUEUR du tapis
    // (ligne verticale dans le couloir), poteaux figurés aux deux extrémités.
    if (m.h) {
      const top = convert(m.x, m.y - m.h / 2), bot = convert(m.x, m.y + m.h / 2);
      ctx.strokeStyle = inkColor(); ctx.lineCap = "round";
      ctx.lineWidth = 3; line(top.px, top.py, bot.px, bot.py);                 // la barre
      ctx.lineWidth = 2.6; line(top.px - 5, top.py, top.px + 5, top.py); line(bot.px - 5, bot.py, bot.px + 5, bot.py); // poteaux (extrémités)
      ctx.lineCap = "butt";
    } else {
      // Vue de profil (poteaux + barre) — repli si la longueur n'est pas fournie.
      ctx.strokeStyle = inkColor(); ctx.lineWidth = 3;
      line(px - 13, py - 9, px - 13, py + 9); line(px + 13, py - 9, px + 13, py + 9);
      ctx.lineWidth = 2.4; line(px - 13, py - 6, px + 13, py - 6);
    }
  } else if (type === "planche") {
    // Planche d'appel (longueur / multi-bond) : latte blanche en travers du couloir.
    ctx.fillStyle = "#FFFFFF"; ctx.strokeStyle = markerStroke(); ctx.lineWidth = 1.6;
    ctx.fillRect(px - 2.5, py - 11, 5, 22); ctx.strokeRect(px - 2.5, py - 11, 5, 22);
  } else if (type === "agres") {
    drawAgres(m);
  } else if (type === "rebond") {
    // Rebond (tennis / tennis de table) : petit repère discret au point d'impact
    // — une ellipse aplatie + un point central (la balle qui touche le sol).
    ctx.save();
    ctx.strokeStyle = MODE === "line" ? "#64748B" : "rgba(255,255,255,.8)";
    ctx.lineWidth = 1.3;
    ctx.beginPath(); ctx.ellipse(px, py, 6, 2.6, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = MODE === "line" ? "#334155" : "rgba(255,255,255,.85)";
    ctx.beginPath(); ctx.arc(px, py, 1.4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  } else if (type === "texte") {
    // TEXTE LIBRE : étiquette neutre posée n'importe où (titre d'atelier,
    // « sens du jeu »…) — chip sombre sur terrain coloré, clair en mode trait.
    const txt = String(m.text || "Texte").slice(0, 40);
    ctx.save();
    ctx.font = "600 13px system-ui, sans-serif";
    const w = ctx.measureText(txt).width + 16, h = 22;
    ctx.fillStyle = MODE === "line" ? "rgba(255,255,255,.95)" : "rgba(15,23,42,.85)";
    ctx.strokeStyle = MODE === "line" ? "#0F172A" : "rgba(255,255,255,.4)"; ctx.lineWidth = 1;
    roundRect(px - w / 2, py - h / 2, w, h, 6); ctx.fill(); ctx.stroke();
    ctx.fillStyle = MODE === "line" ? "#0F172A" : "#FFFFFF";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(txt, px, py + 0.5);
    ctx.restore();
    placed.push({ x: px - w / 2, y: py - h / 2, w, h });
  } else if (type === "cote") {
    // COTE DE DISTANCE : double flèche à butées + texte (« 10 m ») — même
    // style ambré que les dimensions de zones (z.dim). Largeur m.w (terrain),
    // redimensionnable par la poignée du coin.
    // SENS DE LA COTE (👎 prod 08/08 PROD11 : « les 8 mètres de couloir
    // apparaissent avec les plots, mais la flèche indiquant 8 mètres est dans
    // le mauvais sens ! »). La cote ne savait mesurer QUE l'horizontale : une
    // largeur de couloir — qui se lit en y — n'était pas exprimable, et l'IA
    // n'avait d'autre choix que de poser une double-flèche couchée. Une cote
    // porte désormais soit "w" (horizontale, comportement historique), soit
    // "h" (VERTICALE) ; à défaut de "h" le tracé est celui d'avant, à
    // l'identique. Une aire cotée sur ses deux dimensions = deux marqueurs.
    const vertical = isVerticalCote(m);
    const half = vertical ? convertSize(0, +m.h).h / 2 : convertSize(m.w || 20, 0).w / 2;
    const x1 = px - half, x2 = px + half;          // horizontale : bornes en x
    const y1 = py - half, y2 = py + half;          // verticale   : bornes en y
    const col = MODE === "line" ? "#0F172A" : "#FDE68A";
    ctx.save();
    ctx.strokeStyle = col; ctx.lineWidth = 2; ctx.lineCap = "round";
    if (vertical) {
      line(px, y1, px, y2);
      line(px - 6, y1, px + 6, y1); line(px - 6, y2, px + 6, y2);       // butées
      ctx.beginPath(); ctx.moveTo(px - 4, y1 + 8); ctx.lineTo(px, y1 + 1); ctx.lineTo(px + 4, y1 + 8); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px - 4, y2 - 8); ctx.lineTo(px, y2 - 1); ctx.lineTo(px + 4, y2 - 8); ctx.stroke();
    } else {
      line(x1, py, x2, py);
      line(x1, py - 6, x1, py + 6); line(x2, py - 6, x2, py + 6);       // butées
      ctx.beginPath(); ctx.moveTo(x1 + 8, py - 4); ctx.lineTo(x1 + 1, py); ctx.lineTo(x1 + 8, py + 4); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x2 - 8, py - 4); ctx.lineTo(x2 - 1, py); ctx.lineTo(x2 - 8, py + 4); ctx.stroke();
    }
    // L'étiquette d'une cote était coupée NET à 12 caractères — dimensionnée
    // pour « 12,5 m », elle amputait tout libellé écrit par l'IA (salve
    // raquettes : « service court » affiché « service cour »). Même traitement
    // que les autres étiquettes depuis la salve proactive nº1 : repli au
    // dernier ESPACE sur deux lignes, ellipse en dernier recours seulement.
    const txtLines = wrapLabel(m.text || "10 m", 14, 2);
    ctx.font = "700 12px system-ui, sans-serif";
    const tw = Math.max(...txtLines.map((l) => ctx.measureText(l).width)) + 12;
    const th = 17 + (txtLines.length - 1) * 13;
    // L'étiquette se pose À CÔTÉ du trait : au-dessus d'une cote horizontale,
    // à droite d'une cote verticale (sinon elle couche sur sa propre flèche).
    const lx = vertical ? px + 10 + tw / 2 : px, ly = vertical ? py - th / 2 : py - 8 - th;
    ctx.fillStyle = MODE === "line" ? "rgba(255,255,255,.95)" : "#FDE68A";
    roundRect(lx - tw / 2, ly, tw, th, 4); ctx.fill();
    if (MODE === "line") { ctx.strokeStyle = "#0F172A"; ctx.lineWidth = 1; ctx.stroke(); }
    ctx.fillStyle = "#0F172A"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    txtLines.forEach((l, i) => ctx.fillText(l, lx, ly + 8.5 + i * 13));
    ctx.restore();
    placed.push({ x: lx - tw / 2, y: ly, w: tw, h: th });
  } else if (type === "consigne" || type === "bulle") {
    // BULLE DE CONSIGNE (réutilisable) : un observateur/arbitre annonce un signal
    // (« break », « top », une couleur…). Bulle blanche avec une queue pointant
    // vers le point (la personne qui annonce).
    // Consigne : deux lignes plutôt qu'une coupe nette (cf. wrapLabel).
    const txtLines = wrapLabel(m.text || "!", 22, 2);
    ctx.save();
    ctx.font = "700 13px system-ui, sans-serif";
    const w = Math.max(...txtLines.map((l) => ctx.measureText(l).width)) + 20, h = 24 + (txtLines.length - 1) * 14;
    const bx = px - w / 2, by = py - h - 9;
    ctx.fillStyle = "#FFFFFF"; ctx.strokeStyle = "#0F172A"; ctx.lineWidth = 1.8;
    roundRect(bx, by, w, h, 7); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(px - 6, by + h); ctx.lineTo(px, by + h + 8); ctx.lineTo(px + 6, by + h); ctx.closePath();
    ctx.fillStyle = "#FFFFFF"; ctx.fill();
    ctx.beginPath(); ctx.moveTo(px - 6, by + h); ctx.lineTo(px, by + h + 8); ctx.lineTo(px + 6, by + h); ctx.stroke();
    ctx.fillStyle = "#0F172A"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    const ty0 = by + h / 2 + 0.5 - (txtLines.length - 1) * 7;
    txtLines.forEach((l, i) => ctx.fillText(l, px, ty0 + i * 14));
    ctx.restore();
    placed.push({ x: bx, y: by, w, h: h + 8 });
  } else {
    ctx.fillStyle = MODE === "line" ? "#64748B" : "#E2E8F0";
    ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI * 2); ctx.fill();
  }
}

/* AGRÈS de gymnastique (profil schématique), centrés sur (m.x, m.y) et
   dimensionnés par m.w / m.h (coords terrain) : poutre, plan incliné,
   mini-trampoline, cheval, barre fixe, barres asymétriques, mur. */
/* AGRÈS VERTICAL (`m.vertical`) — un agrès était TOUJOURS dessiné à
   l'horizontale : `w` en x, `h` en y, aucune rotation. Quand le mouvement se
   déroule en hauteur (colonne d'ateliers, poutre prise dans la profondeur),
   l'agrès se retrouvait EN TRAVERS de l'action — le reproche de l'enseignant :
   « pas forcément bien orienté par rapport au mouvement ». Mesuré : 9 agrès
   sur 41.

   On ne tourne PAS d'un angle libre : `convertSize` applique des échelles
   différentes en x et en y (le terrain est plus large que haut en pixels), une
   rotation quelconque déformerait le glyphe, et l'éditeur devrait tourner ses
   poignées et son test de survol avec. Le dépôt a déjà tranché ce compromis
   pour la COTE, qui devient verticale via `h` : même choix ici. On calcule les
   extensions DANS LE BON AXE, puis on tourne d'un quart de tour — le glyphe
   reste à ses proportions et la boîte de sélection n'a qu'à échanger w et h. */
function drawAgres(m) {
  const { px, py } = convert(m.x, m.y);
  const vert = m.vertical === true;
  // Vertical : la LONGUEUR (m.w) se lit en y, l'ÉPAISSEUR (m.h) en x.
  const s = vert ? { w: convertSize(0, m.w || 12).h, h: convertSize(m.h || 16, 0).w }
    : convertSize(m.w || 12, m.h || 16);
  const hw = s.w / 2, hh = s.h / 2;
  const OUT = "#1E293B";
  const WOOD = MODE === "line" ? "#FFFFFF" : "#C9A36A";
  const STEEL = MODE === "line" ? "#FFFFFF" : "#AEB6BF";
  ctx.save();
  if (vert) { ctx.translate(px, py); ctx.rotate(-Math.PI / 2); ctx.translate(-px, -py); }
  /* MIROIR (`m.mirror`) — plusieurs agrès ont un SENS : un plan incliné monte
     vers la droite, un tremplin propulse dans une direction, un cheval a son
     appel d'un côté. Le dessin les figeait ; l'enseignant qui installe son
     atelier dans l'autre sens ne pouvait pas le représenter. Le retournement
     s'applique APRÈS la rotation, donc dans le repère de l'agrès : il inverse
     toujours sa longueur, qu'il soit couché ou debout. */
  if (m.mirror === true) { ctx.translate(px, py); ctx.scale(-1, 1); ctx.translate(-px, -py); }
  ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.strokeStyle = OUT;
  switch (m.kind) {
    case "poutre":
      ctx.fillStyle = WOOD; ctx.lineWidth = 1.6;
      roundRect(px - hw, py - 3, s.w, 6, 2); ctx.fill(); ctx.stroke();
      ctx.lineWidth = 2; line(px - hw + 6, py + 3, px - hw + 6, py + hh); line(px + hw - 6, py + 3, px + hw - 6, py + hh);
      break;
    case "plan_incline":
      ctx.fillStyle = MODE === "line" ? "rgba(15,23,42,.10)" : "#8FB8D8"; ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.moveTo(px - hw, py + hh); ctx.lineTo(px + hw, py + hh); ctx.lineTo(px + hw, py - hh); ctx.closePath();
      ctx.fill(); ctx.stroke();
      break;
    case "trampoline": {
      const th = Math.min(hh, 9);
      ctx.fillStyle = MODE === "line" ? "#FFFFFF" : "#2E3A48"; ctx.lineWidth = 1.8;
      roundRect(px - hw, py - th, s.w, th * 2, 4); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = MODE === "line" ? "#64748B" : "#CBD5E1"; ctx.lineWidth = 1;
      line(px - hw + 4, py, px + hw - 4, py);                                   // toile
      break;
    }
    case "cheval": {
      const bh = Math.min(hh, 8);
      ctx.fillStyle = WOOD; ctx.lineWidth = 1.6;
      roundRect(px - hw, py - bh, s.w, bh * 1.4, 3); ctx.fill(); ctx.stroke();
      ctx.lineWidth = 2; line(px - hw + 5, py + bh * 0.4, px - hw + 5, py + hh); line(px + hw - 5, py + bh * 0.4, px + hw - 5, py + hh);
      break;
    }
    case "barre_fixe":
      ctx.strokeStyle = STEEL; ctx.lineWidth = 2.4;
      line(px - hw + 4, py - hh, px - hw + 4, py + hh); line(px + hw - 4, py - hh, px + hw - 4, py + hh);
      ctx.strokeStyle = OUT; ctx.lineWidth = 2.6; line(px - hw + 2, py - hh + 2, px + hw - 2, py - hh + 2);
      break;
    case "barres_asym":
      ctx.strokeStyle = STEEL; ctx.lineWidth = 2.2;
      line(px - hw + 4, py - hh, px - hw + 4, py + hh); line(px + hw - 4, py - hh * 0.1, px + hw - 4, py + hh);
      ctx.strokeStyle = OUT; ctx.lineWidth = 2.4;
      line(px - hw + 2, py - hh + 2, px - 1, py - hh + 2);                      // barre haute
      line(px + 1, py - hh * 0.1 + 2, px + hw - 2, py - hh * 0.1 + 2);          // barre basse
      break;
    case "barres_par": {
      // BARRES PARALLÈLES : deux barres de MÊME hauteur, côte à côte sur toute
      // la longueur — c'est ce qui les distingue des asymétriques, dont les deux
      // barres sont à des hauteurs différentes. Vue de dessus : deux lignes
      // parallèles dans le sens de la progression, sur quatre montants.
      const ecart = Math.min(hh, 7);
      ctx.strokeStyle = STEEL; ctx.lineWidth = 2.2;
      for (const sy of [-ecart, ecart]) {
        line(px - hw + 4, py + sy - 3, px - hw + 4, py + sy + 3);               // montant avant
        line(px + hw - 4, py + sy - 3, px + hw - 4, py + sy + 3);               // montant arrière
      }
      ctx.strokeStyle = OUT; ctx.lineWidth = 2.6;
      line(px - hw + 2, py - ecart, px + hw - 2, py - ecart);
      line(px - hw + 2, py + ecart, px + hw - 2, py + ecart);
      break;
    }
    case "mur":
      ctx.fillStyle = MODE === "line" ? "rgba(15,23,42,.12)" : "#566072"; ctx.lineWidth = 1.6;
      ctx.fillRect(px + hw - 5, py - hh, 5, s.h); ctx.strokeRect(px + hw - 5, py - hh, 5, s.h);
      ctx.strokeStyle = MODE === "line" ? "#64748B" : "#94A3B8"; ctx.lineWidth = 0.8;
      for (let yy = py - hh + 3; yy < py + hh; yy += 5) line(px + hw - 5, yy, px + hw, yy - 3);   // hachures
      break;
    case "tremplin": {
      // Planche d'appel (saut de cheval) : petite planche INCLINÉE, basse à
      // gauche et relevée à droite vers l'agrès franchi.
      const th = Math.min(hh, 7);
      ctx.fillStyle = MODE === "line" ? "rgba(15,23,42,.10)" : "#9AA7B5"; ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.moveTo(px - hw, py + th); ctx.lineTo(px + hw, py - th); ctx.lineTo(px + hw, py + th); ctx.closePath();
      ctx.fill(); ctx.stroke();
      break;
    }
    case "banc": {
      // Banc suédois vu de dessus : plateau bois étroit, pieds aux extrémités.
      const bh = Math.min(hh, 6);
      ctx.fillStyle = WOOD; ctx.lineWidth = 1.6;
      roundRect(px - hw, py - bh, s.w, bh * 2, 3); ctx.fill(); ctx.stroke();
      ctx.lineWidth = 1.2;
      line(px - hw + 4, py - bh, px - hw + 4, py + bh); line(px + hw - 4, py - bh, px + hw - 4, py + bh);
      break;
    }
    case "echelle": {
      // Échelle de rythme : deux montants reliés par des barreaux réguliers.
      const eh = Math.min(hh, 8);
      ctx.lineWidth = 2;
      line(px - hw, py - eh, px + hw, py - eh); line(px - hw, py + eh, px + hw, py + eh);
      ctx.lineWidth = 1.4;
      const n = Math.max(3, Math.round(s.w / 16));
      for (let k = 0; k <= n; k++) { const xx = px - hw + (s.w * k) / n; line(xx, py - eh, xx, py + eh); }
      break;
    }
    case "tapis": {
      // Tapis de réception vu de dessus : rectangle bleu à coins arrondis,
      // liseré intérieur (couture) pour le distinguer d'une simple zone.
      ctx.fillStyle = MODE === "line" ? "rgba(15,23,42,.08)" : "#7FB0D6"; ctx.lineWidth = 1.6;
      roundRect(px - hw, py - hh, s.w, s.h, 5); ctx.fill(); ctx.stroke();
      ctx.strokeStyle = MODE === "line" ? "#64748B" : "rgba(255,255,255,.65)"; ctx.lineWidth = 1;
      roundRect(px - hw + 4, py - hh + 4, s.w - 8, s.h - 8, 4); ctx.stroke();
      break;
    }
    default:
      ctx.fillStyle = WOOD; ctx.lineWidth = 1.6;
      roundRect(px - hw, py - 4, s.w, 8, 2); ctx.fill(); ctx.stroke();
  }
  ctx.restore();
}

function renderPlayers(players, mutedIds) {
  players.forEach((p, i) => {
    const isMuted = !!(mutedIds && mutedIds.has(p.id));
    const { px, py } = convert(p.x, p.y);
    const fill = teamColor(p, i);
    ctx.save();
    if (isMuted) ctx.globalAlpha = 0.32;   // mode simple : présent mais en retrait
    ctx.beginPath(); ctx.arc(px, py, PLAYER_R, 0, Math.PI * 2);
    ctx.fillStyle = fill; ctx.fill();
    ctx.lineWidth = 2.6; ctx.strokeStyle = markerStroke(); ctx.stroke();
    const num = displayNum(p, i);
    ctx.fillStyle = isLightFill(fill) ? "#0F172A" : "#FFFFFF";
    ctx.font = "700 13px system-ui, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(num, px, py + 0.5);
    ctx.restore();
  });
  players.forEach((p, i) => {
    if (!p.role) return;
    if (mutedIds && mutedIds.has(p.id)) return;   // pas d'étiquette sur un joueur estompé
    const { px, py } = convert(p.x, p.y);
    placeLabel(p.role, px, py);
  });
}

/* Position d'arrivée d'un déplacement : cercle évidé, même numéro que le
   joueur, à l'endroit où il termine son mouvement (« position 2 »). */
function drawGhost(px, py, num, color) {
  ctx.save();
  ctx.setLineDash([3, 3]); ctx.lineWidth = 2.4; ctx.strokeStyle = color;
  ctx.fillStyle = MODE === "line" ? "rgba(255,255,255,.92)" : "rgba(255,255,255,.55)";
  ctx.beginPath(); ctx.arc(px, py, PLAYER_R, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = color; ctx.font = "700 12px system-ui, sans-serif";
  ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(num, px, py + 0.5);
  ctx.restore();
  placed.push({ x: px - PLAYER_R, y: py - PLAYER_R, w: 2 * PLAYER_R, h: 2 * PLAYER_R });
}
/* Hexagone évidé numéroté (repère d'ordre), distinct des pastilles joueur. */
function drawHexBadge(cx, cy, text, color) {
  const r = 9.5;
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 6 + i * Math.PI / 3;
    const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
    i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = "#FFFFFF"; ctx.fill();
  ctx.lineWidth = 2; ctx.strokeStyle = color; ctx.stroke();
  ctx.fillStyle = color; ctx.font = "700 11px system-ui, sans-serif";
  ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(text, cx, cy + 0.5);
  placed.push({ x: cx - r, y: cy - r, w: 2 * r, h: 2 * r });
}
function placeHexBadge(mx, my, text, color, A, B) {
  const ang = Math.atan2(B.py - A.py, B.px - A.px);
  const nx = -Math.sin(ang), ny = Math.cos(ang);
  const r = 9.5;
  let cx = mx, cy = my;
  for (const o of [0, 15, -15, 28, -28]) {
    const tx = mx + nx * o, ty = my + ny * o;
    cx = tx; cy = ty;
    if (fits({ x: tx - r, y: ty - r, w: 2 * r, h: 2 * r })) break;
  }
  drawHexBadge(cx, cy, text, color);
  return { x: cx, y: cy };
}

/* Trace un trait selon le style EPS (plein / pointillés / ondulé / épais). */
function strokeStyled(sx, sy, ex, ey, style, color, width) {
  ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.setLineDash([]);
  if (style === "wavy") {
    const dx = ex - sx, dy = ey - sy, len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len, ny = dx / len, amp = 3.4;
    const cycles = Math.max(1, Math.round(len / 16));
    const n = Math.max(8, Math.round(len / 3));
    ctx.beginPath(); ctx.moveTo(sx, sy);
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const off = Math.sin(t * cycles * Math.PI * 2) * amp * Math.sin(t * Math.PI); // 0 aux extrémités
      ctx.lineTo(sx + dx * t + nx * off, sy + dy * t + ny * off);
    }
    ctx.stroke();
  } else {
    if (style === "dashed") ctx.setLineDash([10, 7]);
    ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(ex, ey); ctx.stroke();
    ctx.setLineDash([]);
  }
}
/* Flèche de ROTATION : arc de cercle COURT sur le côté (bulge vers la droite),
   pour figurer le passage d'un atelier au suivant sans traverser le schéma. */
function drawRotationArc(from, to, bulge) {
  const def = ACTIONS.rotation;
  const ctrl = { x: Math.min(99, Math.max(from.x, to.x) + bulge), y: (from.y + to.y) / 2 };
  const A = convert(from.x, from.y), C = convert(ctrl.x, ctrl.y), B = convert(to.x, to.y);
  const curve = (color, w) => {
    ctx.strokeStyle = color; ctx.lineWidth = w; ctx.lineCap = "round"; ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(A.px, A.py); ctx.quadraticCurveTo(C.px, C.py, B.px, B.py); ctx.stroke();
  };
  if (MODE === "color") curve("rgba(255,255,255,.92)", 6);
  curve(def.color, 3);
  // Pointe : tangente à la courbe en B (direction point de contrôle -> B).
  const ang = Math.atan2(B.py - C.py, B.px - C.px), head = 11;
  ctx.fillStyle = def.color;
  ctx.beginPath(); ctx.moveTo(B.px, B.py);
  ctx.lineTo(B.px - head * Math.cos(ang - Math.PI / 6), B.py - head * Math.sin(ang - Math.PI / 6));
  ctx.lineTo(B.px - head * Math.cos(ang + Math.PI / 6), B.py - head * Math.sin(ang + Math.PI / 6));
  ctx.closePath(); ctx.fill();
}
/* Longueurs cumulées d'une polyligne (en unités de scène) + point à l'abscisse
   curviligne t·L. Sert aux trajectoires COURBES : une course qui suit le
   couloir d'une piste, un parcours qui contourne un obstacle. */
export function pathLength(path) {
  let L = 0;
  for (let i = 1; i < path.length; i++) L += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
  return L;
}
/* Tronque une polyligne à la fraction t de sa longueur (0..1). Utilisé par
   l'ANIMATION du diaporama : la pointe glisse le long de la courbe, elle ne
   saute pas d'un bout à l'autre. */
export function trimPath(path, t) {
  const total = pathLength(path);
  if (!(total > 0)) return path.slice(0, 1);
  let target = total * Math.max(0, Math.min(1, t)), out = [path[0]];
  for (let i = 1; i < path.length; i++) {
    const d = Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
    if (d >= target) {
      const k = d > 0 ? target / d : 0;
      out.push({ x: path[i - 1].x + (path[i].x - path[i - 1].x) * k,
        y: path[i - 1].y + (path[i].y - path[i - 1].y) * k });
      return out;
    }
    target -= d; out.push(path[i]);
  }
  return out;
}
/* Flèche SUIVANT UNE POLYLIGNE. Une course de relais qui va d'une tranche à
   l'autre passe par un virage : un segment droit couperait la pelouse. La
   pointe est tangente au dernier segment ; les extrémités reculent d'un rayon
   de pastille, comme une flèche droite. */
function drawPathArrow(path, def) {
  if (!Array.isArray(path) || path.length < 2) return;
  const pts = path.map((q) => convert(q.x, q.y));
  const back = (a, b, d) => {
    const L = Math.hypot(b.px - a.px, b.py - a.py) || 1;
    return { px: a.px + ((b.px - a.px) / L) * d, py: a.py + ((b.py - a.py) / L) * d };
  };
  pts[0] = back(pts[0], pts[1], PLAYER_R + 3);
  pts[pts.length - 1] = back(pts[pts.length - 1], pts[pts.length - 2], PLAYER_R + 7);
  const style = def.style || "solid";
  const width = style === "thick" ? 5.5 : 3;
  const stroke = (color, w) => {
    ctx.strokeStyle = color; ctx.lineWidth = w; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.setLineDash(style === "dashed" ? [10, 7] : []);
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p.px, p.py) : ctx.moveTo(p.px, p.py)));
    ctx.stroke(); ctx.setLineDash([]);
  };
  if (MODE === "color") stroke("rgba(255,255,255,.92)", width + 3);
  stroke(def.color, width);
  const E = pts[pts.length - 1], P = pts[pts.length - 2];
  const ang = Math.atan2(E.py - P.py, E.px - P.px), head = style === "thick" ? 14 : 12;
  ctx.fillStyle = def.color;
  ctx.beginPath(); ctx.moveTo(E.px, E.py);
  ctx.lineTo(E.px - head * Math.cos(ang - Math.PI / 6), E.py - head * Math.sin(ang - Math.PI / 6));
  ctx.lineTo(E.px - head * Math.cos(ang + Math.PI / 6), E.py - head * Math.sin(ang + Math.PI / 6));
  ctx.closePath(); ctx.fill();
}
function drawArrow(x1, y1, x2, y2, def) {
  const style = def.style || "solid";
  const isScreen = style === "screen";
  const width = style === "thick" ? 5.5 : 3;
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const sx = x1 + Math.cos(ang) * (PLAYER_R + 3), sy = y1 + Math.sin(ang) * (PLAYER_R + 3);
  const endGap = isScreen ? PLAYER_R + 3 : PLAYER_R + 7;
  const ex = x2 - Math.cos(ang) * endGap, ey = y2 - Math.sin(ang) * endGap;
  const head = style === "thick" ? 14 : 12;
  const nx = -Math.sin(ang), ny = Math.cos(ang);

  // Liseré clair (mode couleur) pour la lisibilité sur fond saturé.
  if (MODE === "color") {
    strokeStyled(sx, sy, ex, ey, isScreen ? "solid" : style, "rgba(255,255,255,.92)", width + 3);
    if (isScreen) { ctx.strokeStyle = "rgba(255,255,255,.92)"; ctx.lineWidth = width + 3; line(ex + nx * 9, ey + ny * 9, ex - nx * 9, ey - ny * 9); }
  }
  // Trait principal.
  strokeStyled(sx, sy, ex, ey, style, def.color, width);

  if (isScreen) {
    // Barre perpendiculaire (mur de l'écran), pas de pointe de flèche.
    ctx.strokeStyle = def.color; ctx.lineWidth = 3.5; ctx.lineCap = "round";
    line(ex + nx * 9, ey + ny * 9, ex - nx * 9, ey - ny * 9);
  } else {
    // Pointe de flèche.
    ctx.fillStyle = def.color;
    ctx.beginPath(); ctx.moveTo(ex, ey);
    ctx.lineTo(ex - head * Math.cos(ang - Math.PI / 6), ey - head * Math.sin(ang - Math.PI / 6));
    ctx.lineTo(ex - head * Math.cos(ang + Math.PI / 6), ey - head * Math.sin(ang + Math.PI / 6));
    ctx.closePath(); ctx.fill();
  }
}
/* 1re passe de renderActions (PURE, exportée pour les tests) : résout les
   extrémités de chaque flèche À L'INSTANT de l'action, puis fait évoluer la
   position du joueur s'il se déplace (course/conduite/écran).
   ANIMATION (diaporama de projection) : une action portant `animT` (0..1)
   est dessinée PARTIELLEMENT — sa pointe (et le fantôme d'arrivée) glisse
   du départ vers la cible ; l'évolution de position suit la pointe.
   CONTOURNEMENT : une flèche qui passerait SUR un pion étranger à l'action est
   courbée pour l'éviter (`src/detour.js`). Le calcul se fait ICI, sur les
   extrémités PLEINES et avant la troncature d'animation — sinon la courbe
   serait recalculée à chaque image et la flèche ondulerait pendant le
   diaporama. Rien n'est écrit dans la scène : l'éditeur peut déplacer les
   joueurs, la courbe se refait au repaint suivant. */
export function resolveActionSegments(actions, players) {
  // Position COURANTE de chaque joueur, qui évolue au fil de la chronologie.
  const pos = {}; players.forEach((p) => { pos[p.id] = { x: Number(p.x) || 0, y: Number(p.y) || 0 }; });
  const ordered = actions.slice().sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
  const segs = [];
  ordered.forEach((a) => {
    // TRAJECTOIRE COURBE (a.path) : la géométrie vient de la polyligne, pas des
    // extrémités. from/to restent renseignés (pastille fantôme, chronologie,
    // suivi de position) — ils valent les deux bouts de la courbe.
    const authored = Array.isArray(a.path) && a.path.length >= 2
      ? a.path.map((q) => ({ x: Number(q.x), y: Number(q.y) })).filter((q) => Number.isFinite(q.x) && Number.isFinite(q.y))
      : null;
    let path = authored && authored.length >= 2 ? authored : null;
    const from = path ? { ...path[0] }
      : ((a.from && pos[a.from]) ? { ...pos[a.from] } : (a.fromx != null ? { x: a.fromx, y: a.fromy } : null));
    let to = path ? { ...path[path.length - 1] }
      : ((a.to && pos[a.to]) ? { ...pos[a.to] } : (a.tox != null ? { x: a.tox, y: a.toy } : null));
    if (!from || !to) return;
    // La rotation est exclue ICI : `drawRotationArc` trace déjà sa courbe et
    // ignore `path` — lui en poser un ne changerait que l'animation, en la
    // faisant sur-bomber. (`arrowDetour` sait rendre cet arc : c'est le banc
    // qui s'en sert, pour mesurer le tracé réellement dessiné.)
    if (!path && a.type !== "rotation") path = arrowDetour(a, from, to, players, pos);
    if (a.animT != null && a.animT < 1) {
      if (path) { path = trimPath(path, a.animT); to = { ...path[path.length - 1] }; }
      else to = { x: from.x + (to.x - from.x) * a.animT, y: from.y + (to.y - from.y) * a.animT };
    }
    segs.push({ a, from, to, ...(path ? { path } : {}) });
    // Le suivi de position dépend de l'action DÉCRITE, pas du contournement :
    // seule une courbe AUTEUR (le relais) vaut déplacement, une courbe
    // d'évitement ne déplace personne qui ne se déplaçait pas déjà.
    if ((a.type === "course" || a.type === "dribble" || a.type === "ecran") && (a.tox != null || authored) && a.from && pos[a.from]) {
      pos[a.from] = { x: to.x, y: to.y };   // le joueur a rejoint la (nouvelle) position — animée le cas échéant
    }
  });
  return segs;
}

/* BALLON FANTÔME (PUR, exporté pour les tests) : position du ballon à l'ISSUE
   de la chronologie visible — même suivi de porteur que qualityIssues
   (analysis.js) : le ballon change de mains sur passe/centre/renvoi, part au
   tir, VOYAGE avec son porteur en conduite ; une course ne le déplace jamais.
   Neutralisé quand la scène a plusieurs ballons (files/couloirs : pas de
   porteur unique) ou quand le ballon n'a pas bougé (pas de doublon de glyphe).
   Renvoie { x, y, seg, viaPlayer } ou null. */
const BALL_GLYPHS = ["ballon", "ball", "balle", "disque", "frisbee", "volant"];
const BALL_MOVES = new Set(["passe", "centre", "renvoi", "tir", "pointer"]);
export function ballGhost(segs, players, markers) {
  const balls = (markers || []).filter((m) => BALL_GLYPHS.includes((m.type || "").toLowerCase()));
  if (balls.length !== 1) return null;
  const ball = balls[0];
  // Porteur initial : le joueur posé à côté du ballon (à défaut, 1er émetteur).
  let holder = null, best = 9;
  players.forEach((p) => {
    const d = Math.hypot((Number(p.x) || 0) - ball.x, (Number(p.y) || 0) - ball.y);
    if (d < best) { best = d; holder = p.id; }
  });
  let out = null;
  for (const s of segs) {
    const a = s.a, t = a.type;
    if (BALL_MOVES.has(t)) {
      out = { x: s.to.x, y: s.to.y, seg: s, viaPlayer: !!a.to };
      holder = t === "tir" ? null : (a.to || null);
    } else if (t === "dribble" && a.from) {
      if (holder == null) holder = a.from;   // 1er porteur déduit (convention qualityIssues)
      if (a.from === holder) out = { x: s.to.x, y: s.to.y, seg: s, viaPlayer: false };
    }
  }
  if (!out || Math.hypot(out.x - ball.x, out.y - ball.y) < 6) return null;
  return out;
}

/* Ballon « fantôme » : même glyphe que le marqueur ballon, contour POINTILLÉ
   et corps translucide — le vocabulaire visuel des fantômes de joueurs. */
function drawBallGhost(px, py) {
  ctx.save();
  ctx.setLineDash([3, 3]);
  ctx.fillStyle = MODE === "line" ? "rgba(255,255,255,.92)" : "rgba(255,255,255,.62)";
  ctx.strokeStyle = "#111"; ctx.lineWidth = 1.6;
  ctx.beginPath(); ctx.arc(px, py, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = "rgba(17,17,17,.6)"; ctx.beginPath(); ctx.arc(px, py, 2.2, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
  placed.push({ x: px - 9, y: py - 9, w: 18, h: 18 });
}

/* DÉCONFLICTION DES TRAJECTOIRES DE BALLE (PURE, exportée pour les tests) —
   retour prod 👎 : « les renvois du facilitateur se confondent avec les
   retours smashés ». Deux trajectoires de balle (passe/renvoi/tir/centre)
   quasi COLINÉAIRES et superposées (<7°, extrémités à <3.2) sont illisibles :
   on DÉCALE la plus tardive de 3.5 unités perpendiculairement AU DESSIN —
   la scène n'est jamais modifiée. Les allers-retours d'un rallye se lisent
   ainsi côte à côte, comme sur un tableau d'entraîneur. */
const BALL_ARROWS = new Set(["passe", "renvoi", "tir", "centre"]);
export function deconflictBallSegments(segs) {
  const sd = (px, py, ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
    if (L2 < 1e-6) return Math.hypot(px - ax, py - ay);
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L2));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  };
  const out = segs.map((s) => ({ ...s, from: { ...s.from }, to: { ...s.to } }));
  const kept = [];
  for (const s of out) {
    // Une trajectoire courbe n'est jamais décalée : le décalage porte sur les
    // extrémités, la polyligne resterait en place et la flèche se disloquerait.
    const isBall = !s.path && BALL_ARROWS.has(s.a.type) && Math.hypot(s.to.x - s.from.x, s.to.y - s.from.y) >= 10;
    if (!isBall) continue;
    let clashes = 0;
    for (const k of kept) {
      const a1 = Math.atan2(s.to.y - s.from.y, s.to.x - s.from.x);
      const a2 = Math.atan2(k.to.y - k.from.y, k.to.x - k.from.x);
      let diff = Math.abs(a1 - a2) * 180 / Math.PI; if (diff > 180) diff = 360 - diff;
      if (Math.min(diff, 180 - diff) > 7) continue;
      const dmax = Math.max(sd(s.from.x, s.from.y, k.from.x, k.from.y, k.to.x, k.to.y),
        sd(s.to.x, s.to.y, k.from.x, k.from.y, k.to.x, k.to.y));
      if (dmax < 3.2) clashes++;
    }
    if (clashes) {
      // Décalage perpendiculaire, en alternant le côté si plusieurs conflits.
      const ang = Math.atan2(s.to.y - s.from.y, s.to.x - s.from.x);
      const off = 3.5 * Math.ceil(clashes / 2) * (clashes % 2 ? 1 : -1);
      const nx = -Math.sin(ang) * off, ny = Math.cos(ang) * off;
      const cl = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      s.from.x = cl(s.from.x + nx, 0, 100); s.from.y = cl(s.from.y + ny, 0, 100);
      s.to.x = cl(s.to.x + nx, 0, 100); s.to.y = cl(s.to.y + ny, 0, 100);
    }
    kept.push(s);
  }
  return out;
}

function renderActions(actions, players, markers) {
  const byId = {}; players.forEach((p) => { byId[p.id] = p; });
  const segs = deconflictBallSegments(resolveActionSegments(actions, players));
  LAST_SEGS = segs.map((s) => ({ a: s.a, from: s.from, to: s.to }));

  // Flèches (les actions « muted » — secondaires en mode simple — sont atténuées)
  segs.forEach((s) => {
    ctx.save(); if (s.a.muted) ctx.globalAlpha = 0.32;
    if (s.a.type === "rotation") drawRotationArc(s.from, s.to, s.a.bulge || 7);
    else if (s.path) drawPathArrow(s.path, actionDef(s.a.type));
    else { const A = convert(s.from.x, s.from.y), B = convert(s.to.x, s.to.y); drawArrow(A.px, A.py, B.px, B.py, actionDef(s.a.type)); }
    ctx.restore();
  });
  // Positions d'arrivée (déplacement d'un joueur vers une zone) : cercle évidé.
  segs.forEach((s) => {
    const a = s.a;
    if (!(a.type === "course" || a.type === "dribble") || a.tox == null) return;
    const p = a.from ? byId[a.from] : null;
    if (!p && a.mark == null) return;
    const num = p ? displayNum(p) : String(a.mark);
    const e = convert(s.to.x, s.to.y);
    ctx.save(); if (a.muted) ctx.globalAlpha = 0.32;
    drawGhost(e.px, e.py, num, p ? teamColor(p, 0) : "#0072B2");
    ctx.restore();
  });
  // BALLON FANTÔME : le marqueur ballon statique reste à l'engagement ; le
  // fantôme montre où le ballon TERMINE la chronologie visible — donc l'étape
  // courante en diaporama, et il GLISSE le long de l'action pendant l'animation.
  const bg = ballGhost(segs, players, markers);
  if (bg) {
    const e = convert(bg.x, bg.y);
    let px = e.px, py = e.py;
    // Arrivée SUR un joueur (passe reçue) : posé à la pointe de la flèche, pas
    // caché sous la pastille. Pendant l'animation, le ballon vole : point exact.
    if (bg.viaPlayer && !(bg.seg.a.animT != null && bg.seg.a.animT < 1)) {
      const A = convert(bg.seg.from.x, bg.seg.from.y);
      const ang = Math.atan2(e.py - A.py, e.px - A.px);
      px = e.px - Math.cos(ang) * (PLAYER_R + 10);
      py = e.py - Math.sin(ang) * (PLAYER_R + 10);
    }
    drawBallGhost(px, py);
  }
  // Repères d'ordre (chronologie) : hexagone évidé au milieu de la flèche.
  // Pendant l'animation d'un temps, son hexagone n'apparaît qu'à l'arrivée.
  LAST_BADGES = [];
  segs.forEach((s) => {
    if (s.a.order == null || s.a.muted || (s.a.animT != null && s.a.animT < 1)) return;
    // Sur une trajectoire courbe, le milieu du segment droit tomberait à côté
    // de la courbe (voire dans la pelouse) : on prend le milieu CURVILIGNE.
    const mid = s.path ? trimPath(s.path, 0.5).slice(-1)[0] : null;
    const A = convert(s.from.x, s.from.y), B = convert(s.to.x, s.to.y);
    const M = mid ? convert(mid.x, mid.y) : { px: (A.px + B.px) / 2, py: (A.py + B.py) / 2 };
    const pos = placeHexBadge(M.px, M.py, String(s.a.order), actionDef(s.a.type).color, A, B);
    LAST_BADGES.push({ a: s.a, x: pos.x, y: pos.y });
  });
  // Les flèches deviennent des obstacles : les étiquettes (rôles, repères)
  // placées ensuite éviteront de se poser SUR le trajet.
  segs.forEach((s) => {
    const legs = s.path ? s.path.slice(1).map((q, i) => [s.path[i], q]) : [[s.from, s.to]];
    for (const [p1, p2] of legs) {
      const A = convert(p1.x, p1.y), B = convert(p2.x, p2.y);
      const len = Math.hypot(B.px - A.px, B.py - A.py);
      const n = Math.max(1, Math.round(len / 24));
      for (let i = 0; i <= n; i++) {
        const tx = A.px + (B.px - A.px) * i / n, ty = A.py + (B.py - A.py) * i / n;
        placed.push({ x: tx - 6, y: ty - 6, w: 12, h: 12 });
      }
    }
  });
}

/* --------------------------------------------------------------------------
   SÉLECTION (éditeur) : halo + poignées par-dessus l'élément sélectionné.
   -------------------------------------------------------------------------- */
const SELECT_COLOR = "#1366D6";
function selRing(x, y, r) {
  ctx.strokeStyle = SELECT_COLOR; ctx.lineWidth = 2.5;
  ctx.setLineDash([5, 4]);
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
  ctx.setLineDash([]);
}
/* POIGNÉES D'UN AGRÈS SÉLECTIONNÉ, en coordonnées TERRAIN — calculées ICI et
   nulle part ailleurs : le dessin (drawSelection) et le test de survol
   (editor.js) les lisent tous deux, ce qui interdit qu'ils divergent. C'est
   la leçon du couple selRect / resizeCorner, qui devaient déjà « correspondre »
   à la main.
   Quart de tour en HAUT À DROITE, miroir en HAUT À GAUCHE ; le
   redimensionnement garde son coin bas-droit. */
export function agresHandles(m) {
  if (!m || String(m.type || "").toLowerCase() !== "agres") return null;
  const box = m.vertical === true ? { w: m.h || 16, h: m.w || 12 } : { w: m.w || 12, h: m.h || 16 };
  return {
    rotate: { x: +m.x + box.w / 2, y: +m.y - box.h / 2 },
    mirror: { x: +m.x - box.w / 2, y: +m.y - box.h / 2 },
  };
}
function selHandle(x, y) {
  ctx.fillStyle = "#FFFFFF"; ctx.strokeStyle = SELECT_COLOR; ctx.lineWidth = 2.2;
  ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
}
/* Halo rectangulaire (agrès, zone) + poignée de REDIMENSIONNEMENT au coin
   bas-droit — la position de la poignée doit correspondre au hit-test de
   l'éditeur (resizeCorner, editor.js). */
function selRect(x1, y1, x2, y2, hx = x2, hy = y2) {
  ctx.strokeStyle = SELECT_COLOR; ctx.lineWidth = 2.5; ctx.setLineDash([5, 4]);
  ctx.strokeRect(x1 - 3, y1 - 3, (x2 - x1) + 6, (y2 - y1) + 6);
  ctx.setLineDash([]);
  selHandle(hx, hy);
}
function drawSelection(sel) {
  if (!sel || !sel.ref) return;
  ctx.save();
  if (sel.kind === "player") {
    const c = convert(sel.ref.x, sel.ref.y);
    selRing(c.px, c.py, PLAYER_R + 6);
  } else if (sel.kind === "zone") {
    const z = sel.ref;
    const a = convert(z.x, z.y), b = convert(z.x + (z.w || 0), z.y + (z.h || 0));
    selRect(a.px, a.py, b.px, b.py);
  } else if (sel.kind === "marker") {
    const m = sel.ref;
    const t = String(m.type || "").toLowerCase();
    // Boîtes redimensionnables : agrès et buts/cages — mêmes défauts que
    // markerBox (editor.js) et drawMarker.
    // Agrès VERTICAL : la boîte échange ses dimensions (cf. drawAgres).
    const box = t === "agres" ? (m.vertical === true ? { w: m.h || 16, h: m.w || 12 } : { w: m.w || 12, h: m.h || 16 })
      : ["but", "goal", "minibut", "cage", "panier", "but-attaque"].includes(t) ? { w: m.w || 3, h: m.h || 2 }
      : null;
    if (box) {
      const a = convert(m.x - box.w / 2, m.y - box.h / 2);
      const b = convert(m.x + box.w / 2, m.y + box.h / 2);
      selRect(a.px, a.py, b.px, b.py);
      const hs = agresHandles(m);
      if (hs) {
        const r = convert(hs.rotate.x, hs.rotate.y), mi = convert(hs.mirror.x, hs.mirror.y);
        selHandle(r.px, r.py); selHandle(mi.px, mi.py);
        ctx.fillStyle = SELECT_COLOR; ctx.font = "700 11px system-ui, sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText("⟳", r.px, r.py + 0.5);      // quart de tour
        ctx.fillText("⇄", mi.px, mi.py + 0.5);     // miroir
      }
    } else if (String(m.type) === "cote") {
      // Halo autour de la ligne de cote ; poignée au bout DROIT (horizontale)
      // ou au bout BAS (verticale) — même règle de sens que le tracé.
      const vert = isVerticalCote(m);
      const a = vert ? convert(m.x, m.y - +m.h / 2) : convert(m.x - (m.w || 20) / 2, m.y);
      const b = vert ? convert(m.x, m.y + +m.h / 2) : convert(m.x + (m.w || 20) / 2, m.y);
      if (vert) selRect(a.px - 10, a.py, b.px + 10, b.py, b.px, b.py);
      else selRect(a.px, a.py - 10, b.px, b.py + 10, b.px, b.py);
    } else {
      const c = convert(m.x, m.y);
      selRing(c.px, c.py, 16);
    }
  } else if (sel.kind === "action") {
    const seg = LAST_SEGS.find((s) => s.a === sel.ref);
    if (seg) {
      const A = convert(seg.from.x, seg.from.y), B = convert(seg.to.x, seg.to.y);
      ctx.strokeStyle = "rgba(255,255,255,.55)"; ctx.lineWidth = 13; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(A.px, A.py); ctx.lineTo(B.px, B.py); ctx.stroke();
      ctx.strokeStyle = "rgba(19,102,214,.5)"; ctx.lineWidth = 8;
      ctx.beginPath(); ctx.moveTo(A.px, A.py); ctx.lineTo(B.px, B.py); ctx.stroke();
      selHandle(A.px, A.py); selHandle(B.px, B.py);
    }
  }
  ctx.restore();
}

/* --------------------------------------------------------------------------
   BANDEAU TITRE & LÉGENDE
   -------------------------------------------------------------------------- */
function drawTitleBand(data) {
  const isEx = data?.kind === "exercise";
  const pad = 18, by = 18, bh = 28;
  // Badge Match/Exercice
  const label = isEx ? "EXERCICE" : "MATCH";
  ctx.font = "700 12px system-ui, sans-serif";
  const bw = ctx.measureText(label).width + 26;
  ctx.fillStyle = isEx ? "#ECFDF3" : "#E8F1FC";
  roundRect(pad, by, bw, bh, 14); ctx.fill();
  ctx.strokeStyle = isEx ? "#A7E8C2" : "#9DC2F0"; ctx.lineWidth = 1; ctx.stroke();
  ctx.fillStyle = isEx ? "#15803D" : "#0F57BC"; ctx.beginPath();
  ctx.arc(pad + 13, by + bh / 2, 3.5, 0, Math.PI * 2); ctx.fill();
  ctx.textAlign = "left"; ctx.textBaseline = "middle";
  ctx.fillText(label, pad + 22, by + bh / 2 + 0.5);
  // Titre
  ctx.fillStyle = "#0F172A"; ctx.font = "700 20px system-ui, sans-serif";
  let t = data?.title || (isEx ? "Situation d'apprentissage" : "Schéma de match");
  const maxW = W - (pad + bw + 14) - pad;
  while (ctx.measureText(t).width > maxW && t.length > 4) t = t.slice(0, -2);
  if (t !== (data?.title || "")) { /* peut être tronqué */ }
  ctx.fillText(t, pad + bw + 14, by + bh / 2 + 1);
  // Séparateur sous le bandeau
  ctx.strokeStyle = "#E6EBF1"; ctx.lineWidth = 1;
  line(0, FIELD.y - 0.5, W, FIELD.y - 0.5);
}

function legendItems(data) {
  const present = new Set((data?.actions || []).map((a) => a.type).filter((t) => ACTIONS[t]));
  const moves = [...present].map((t) => ({ ...actionDef(t), type: t }));
  const players = data?.players || [];
  const markers = data?.markers || [];
  const ent = [];
  if (players.some((p) => !["B", "C", "OBS", "k"].includes(p.team) && !/gardien/i.test(p.role || "")))
    ent.push({ glyph: "circle", color: "#0072B2", label: "Équipe A" });
  if (players.some((p) => p.team === "B")) ent.push({ glyph: "circle", color: "#D55E00", label: "Équipe B" });
  if (players.some((p) => p.team === "C")) ent.push({ glyph: "circle", color: "#009E73", label: "Équipe C" });
  if (players.some((p) => p.team === "k" || /gardien/i.test(p.role || "")))
    ent.push({ glyph: "circle", color: "#F2C200", label: "Gardien" });
  if (players.some((p) => p.team === "OBS")) ent.push({ glyph: "circle", color: "#F2C200", label: "Observateur" });
  const mtypes = new Set(markers.map((m) => (m.type || "").toLowerCase()));
  if ([...mtypes].some((t) => ["plot", "cone", "cône", "plots", "coupelle"].includes(t)))
    ent.push({ glyph: "cone", color: "#EA7317", label: "Cône" });
  if ([...mtypes].some((t) => ["ballon", "ball", "balle"].includes(t)))
    ent.push({ glyph: "ball", color: "#fff", label: "Ballon" });
  if (mtypes.has("disque") || mtypes.has("frisbee")) ent.push({ glyph: "disc", color: "#fff", label: "Disque" });
  if (mtypes.has("boule")) ent.push({ glyph: "dot", color: "#9AA3AD", label: "Boule" });
  if (mtypes.has("cochonnet")) ent.push({ glyph: "dot", color: "#E6AB02", label: "Cochonnet" });
  if (mtypes.has("temoin")) ent.push({ glyph: "dot", color: "#D55E00", label: "Témoin" });
  if (mtypes.has("essai")) ent.push({ glyph: "ball", color: "#fff", label: "Essai / aplatir" });
  if (mtypes.has("agres")) ent.push({ glyph: "agres", color: "#C9A36A", label: "Agrès" });
  if (mtypes.has("rebond")) ent.push({ glyph: "rebond", color: "#64748B", label: "Rebond" });
  if (mtypes.has("casier") || mtypes.has("caisse")) ent.push({ glyph: "casier", color: "#B45309", label: "Casier à ballons" });
  return { moves, ent };
}
function legendHeight(data) {
  const { moves, ent } = legendItems(data);
  const rows = (moves.length ? 1 : 0) + (ent.length ? 1 : 0);
  return rows ? 16 + rows * 24 : 24;
}
function drawLegend(data) {
  const { moves, ent } = legendItems(data);
  const top = FIELD.y + FIELD.h;
  ctx.strokeStyle = "#E6EBF1"; ctx.lineWidth = 1; line(0, top + 0.5, W, top + 0.5);
  let y = top + 18;
  const drawRow = (title, items, drawer) => {
    let x = 18;
    ctx.fillStyle = "#64748B"; ctx.font = "700 11px system-ui, sans-serif";
    ctx.textAlign = "left"; ctx.textBaseline = "middle";
    ctx.fillText(title, x, y); x += ctx.measureText(title).width + 20;
    items.forEach((it, i) => { x = drawer(it, x, y, i === items.length - 1); });
    y += 24;
  };
  if (moves.length) drawRow("MOUVEMENTS", moves, (it, x, yy, last) => {
    const sx = x, sw = 28, isScreen = it.style === "screen";
    const w = it.style === "thick" ? 4.5 : 2.8;
    strokeStyled(sx, yy, sx + sw - (isScreen ? 0 : 6), yy, it.style, it.color, w);
    if (isScreen) { ctx.strokeStyle = it.color; ctx.lineWidth = 3; ctx.setLineDash([]); line(sx + sw, yy - 6, sx + sw, yy + 6); }
    else {
      ctx.fillStyle = it.color; ctx.beginPath(); ctx.moveTo(sx + sw, yy);
      ctx.lineTo(sx + sw - 9, yy - 4); ctx.lineTo(sx + sw - 9, yy + 4); ctx.closePath(); ctx.fill();
    }
    ctx.fillStyle = "#334155"; ctx.font = "500 12px system-ui, sans-serif";
    ctx.textAlign = "left"; ctx.textBaseline = "middle";
    const tx = sx + sw + 7; ctx.fillText(it.label, tx, yy);
    const end = tx + ctx.measureText(it.label).width;
    // séparateur discret entre items (la légende ne « colle » plus)
    if (!last) { ctx.fillStyle = "#CBD5E1"; ctx.fillText("·", end + 14, yy); }
    return end + 30;
  });
  if (ent.length) drawRow("REPÈRES", ent, (it, x, yy, last) => {
    if (it.glyph === "cone") {
      ctx.fillStyle = it.color; ctx.strokeStyle = "#334155"; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x + 7, yy - 6); ctx.lineTo(x, yy + 6); ctx.lineTo(x + 14, yy + 6);
      ctx.closePath(); ctx.fill(); ctx.stroke();
    } else if (it.glyph === "ball") {
      ctx.fillStyle = "#fff"; ctx.strokeStyle = "#111"; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(x + 7, yy, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    } else if (it.glyph === "disc") {
      ctx.save(); ctx.translate(x + 7, yy); ctx.scale(1, 0.5);
      ctx.fillStyle = "#fff"; ctx.strokeStyle = "#111"; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(0, 0, 6.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); ctx.restore();
    } else if (it.glyph === "agres") {
      // Glyphe agrès : petite latte sur deux pieds (évoque poutre/cheval).
      ctx.fillStyle = it.color; ctx.strokeStyle = "#334155"; ctx.lineWidth = 1.2;
      roundRect(x, yy - 4, 14, 4, 1.5); ctx.fill(); ctx.stroke();
      ctx.lineWidth = 1.4; line(x + 3, yy, x + 3, yy + 6); line(x + 11, yy, x + 11, yy + 6);
    } else if (it.glyph === "rebond") {
      ctx.strokeStyle = "#64748B"; ctx.lineWidth = 1.3;
      ctx.beginPath(); ctx.ellipse(x + 7, yy, 6, 2.6, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = "#334155"; ctx.beginPath(); ctx.arc(x + 7, yy, 1.4, 0, Math.PI * 2); ctx.fill();
    } else if (it.glyph === "casier") {
      // Glyphe casier : petite caisse trapézoïdale + trois ballons.
      ctx.fillStyle = it.color; ctx.strokeStyle = "#334155"; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(x, yy + 6); ctx.lineTo(x + 14, yy + 6); ctx.lineTo(x + 12, yy); ctx.lineTo(x + 2, yy); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.lineWidth = 1;
      for (const dx of [3, 7, 11]) { ctx.beginPath(); ctx.arc(x + dx, yy - 1.5, 2, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); }
    } else {
      ctx.fillStyle = it.color; ctx.strokeStyle = "#334155"; ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.arc(x + 7, yy, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    ctx.fillStyle = "#334155"; ctx.font = "500 12px system-ui, sans-serif";
    ctx.textAlign = "left"; ctx.textBaseline = "middle";
    const tx = x + 20; ctx.fillText(it.label, tx, yy);
    const end = tx + ctx.measureText(it.label).width;
    if (!last) { ctx.fillStyle = "#CBD5E1"; ctx.fillText("·", end + 14, yy); }
    return end + 30;
  });
}

/* --------------------------------------------------------------------------
   Orchestration
   -------------------------------------------------------------------------- */
function draw(data, sport, opts = {}) {
  const compact = !!opts.compact;
  placed = [];
  const titleH = compact ? 6 : 56;
  const legH = compact ? 6 : legendHeight(data);
  FIELD = { x: compact ? 6 : 22, y: titleH, w: W - (compact ? 12 : 44), h: H - titleH - legH };

  TERRAIN_OPTS = (data && data.terrainOpts) || {};
  HALF = data?.meta?.halfCourt === "right";
  drawFieldBg(sport);
  // Aire NEUTRE (atelier hors sport, ex. lancer sur cible) : rectangle simple,
  // quel que soit le sport détecté.
  if (TERRAIN_OPTS.plain) TERRAINS.other();
  else if (HALF) {
    // DEMI-TERRAIN : les tracés du sport sont dessinés sur une boîte virtuelle
    // DOUBLE (débordant à gauche), clippée à la boîte réelle — on ne voit que la
    // moitié droite, à l'échelle exacte de convert(). La ligne médiane du
    // terrain complet tombe pile sur le bord gauche, le but/panier à droite.
    // Aucune variante de tracé par sport à écrire ni à maintenir.
    const f = FIELD;
    ctx.save(); ctx.beginPath(); ctx.rect(f.x - 2, f.y - 2, f.w + 4, f.h + 4); ctx.clip();
    FIELD = { x: f.x - f.w, y: f.y, w: 2 * f.w, h: f.h };
    HALF_BG = true;   // arcs -> ellipses de rayon x doublé (voir arc()/circle())
    try { (TERRAINS[sport] || TERRAINS.other)(); } finally { HALF_BG = false; FIELD = f; ctx.restore(); }
    // Bord gauche (= ligne médiane) : retracé net, la boîte virtuelle l'a clippé.
    ctx.strokeStyle = LINE; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(f.x, f.y); ctx.lineTo(f.x, f.y + f.h); ctx.stroke();
  } else (TERRAINS[sport] || TERRAINS.other)();

  // Adoucit le terrain pour un exercice (le dessin pédagogique doit primer).
  if (data?.kind === "exercise" && MODE === "color") {
    ctx.fillStyle = "rgba(255,255,255,.14)"; ctx.fillRect(FIELD.x, FIELD.y, FIELD.w, FIELD.h);
  }

  const zones = Array.isArray(data?.zones) ? data.zones : [];
  const markers = Array.isArray(data?.markers) ? data.markers : [];
  const players = Array.isArray(data?.players) ? data.players : [];
  const actions = Array.isArray(data?.actions) ? data.actions : [];

  renderZones(zones);
  markers.forEach(drawMarker);
  // Les pastilles joueurs sont des OBSTACLES dès maintenant : les numéros
  // d'étapes (hexagones) placés avec les flèches ne se cacheront pas dessous.
  players.forEach((p) => { const c = convert(p.x, p.y); placed.push({ x: c.px - PLAYER_R, y: c.py - PLAYER_R, w: 2 * PLAYER_R, h: 2 * PLAYER_R }); });
  renderActions(actions, players, markers);
  // APRÈS les flèches : sur une piste, les trajectoires occupent toute la
  // largeur des couloirs et deviennent des obstacles — le libellé se pose
  // alors dans la pelouse, à côté de son ruban, et non par-dessus une pastille
  // ou un numéro d'étape.
  renderBandZoneLabels(zones);
  // Impacts de rebond dessinés d'office (tennis / TT) aux retombées des frappes.
  for (const pt of autoRebondPoints(sport, LAST_SEGS, markers)) {
    const { px, py } = convert(pt.x, pt.y);
    ctx.save();
    ctx.strokeStyle = MODE === "line" ? "#64748B" : "rgba(255,255,255,.8)";
    ctx.lineWidth = 1.3;
    ctx.beginPath(); ctx.ellipse(px, py, 6, 2.6, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = MODE === "line" ? "#334155" : "rgba(255,255,255,.85)";
    ctx.beginPath(); ctx.arc(px, py, 1.4, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
  renderPlayers(players, opts.mutedPlayers);
  // Étiquettes de repères nommés (buts, cibles…) après tout le reste.
  markers.forEach((m) => { if (m.label) { const p = convert(m.x, m.y); placeLabel(m.label, p.px, p.py, { gap: 12 }); } });
  drawSelection(opts.selection);

  if (!compact) { drawTitleBand(data); drawLegend(data); }
  return { kind: data?.kind === "exercise" ? "exercise" : "match", players: players.length, actions: actions.length, zones: zones.length };
}

export function renderScene(data, sport, mode, opts = {}) {
  if (mode) setRenderMode(mode);
  const r = draw(data, sport, opts);
  MAIN = { fx: FIELD.x, fy: FIELD.y, fw: FIELD.w, fh: FIELD.h, hc: HALF };
  return r;
}
export function renderToCanvas(targetCanvas, data, sport, mode, opts = {}) {
  const sc = canvas, sx = ctx, sW = W, sH = H, sM = MODE, sL = LINE, sF = FIELD, sS = LAST_SEGS, sB = LAST_BADGES, sHalf = HALF;
  canvas = targetCanvas; ctx = targetCanvas.getContext("2d");
  // opts.scale (>1) : rendu NET sur grand canvas (mode projection) — la mise en
  // page garde sa géométrie de référence (tailles de police, rayons, épaisseurs)
  // et c'est la transformation qui multiplie les pixels.
  const k = opts.scale > 0 ? opts.scale : 1;
  W = targetCanvas.width / k; H = targetCanvas.height / k;
  setRenderMode(mode);
  try { ctx.setTransform(k, 0, 0, k, 0, 0); draw(data, sport, opts); }
  finally {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // opts.track : ce canvas devient la CIBLE D'ÉDITION (mode projection
    // « Schéma complet ») — on publie sa géométrie pour canvasToField et on
    // GARDE ses segments/badges pour le hit-test de l'éditeur, au lieu de
    // restaurer ceux du canvas principal. À utiliser avec scale = 1 (les
    // coordonnées éditeur sont en pixels internes du canvas).
    if (opts.track) MAIN = { fx: FIELD.x, fy: FIELD.y, fw: FIELD.w, fh: FIELD.h, hc: HALF };
    canvas = sc; ctx = sx; W = sW; H = sH; MODE = sM; LINE = sL; FIELD = sF; HALF = sHalf;
    if (!opts.track) { LAST_SEGS = sS; LAST_BADGES = sB; }
  }
}
/* VIGNETTE de bibliothèque : dessine UN élément (marqueur ou pastille de
   joueur) centré sur un petit canvas, fond terrain neutre — mêmes routines
   que le schéma (drawMarker/drawAgres), donc toujours fidèles au rendu réel.
   `item.icon` (catalogue) surcharge zoom / position / texte / dimensions pour
   les éléments encombrants (bulle, cote, agrès). État du moteur restauré. */
export function renderElementIcon(targetCanvas, item) {
  const sc = canvas, sx = ctx, sW = W, sH = H, sM = MODE, sL = LINE, sF = FIELD, sS = LAST_SEGS, sB = LAST_BADGES, sHalf = HALF;
  HALF = false;   // les vignettes sont dessinées HORS de draw() : jamais de transformation demi-terrain
  canvas = targetCanvas; ctx = targetCanvas.getContext("2d");
  const icon = item.icon || {};
  const k = icon.zoom || 2;                       // ×2 : tailles fixes (px) lisibles en petit
  W = targetCanvas.width / k; H = targetCanvas.height / k;
  MODE = "color"; LINE = "#ffffff";
  FIELD = { x: 0, y: 0, w: W, h: H };
  try {
    ctx.setTransform(k, 0, 0, k, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#3D8A63";                    // vert terrain : traits blancs lisibles
    roundRect(0, 0, W, H, 5); ctx.fill();
    if (item.player) {
      ctx.beginPath(); ctx.arc(W / 2, H / 2, 12, 0, Math.PI * 2);
      ctx.fillStyle = item.player.color || "#F2C200"; ctx.fill();
      ctx.lineWidth = 2.4; ctx.strokeStyle = "#1E293B"; ctx.stroke();
    } else if (item.marker) {
      const mm = { ...item.marker, x: 50, y: 55, ...icon };
      delete mm.zoom;
      drawMarker(mm);
    }
  } finally {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    canvas = sc; ctx = sx; W = sW; H = sH; MODE = sM; LINE = sL; FIELD = sF; LAST_SEGS = sS; LAST_BADGES = sB; HALF = sHalf;
  }
}
export function exportPng(sport) {
  const link = document.createElement("a");
  link.download = "schema-" + (sport || "tactique") + ".png";
  link.href = canvas.toDataURL("image/png");
  link.click();
}
export function getPngDataUrl() { return canvas.toDataURL("image/png"); }
